 import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../../components/Sidebar";
import StatusBanner from "../../../components/StatusBanner";
import { getPermissionControllers, hasCustomerPermission, hasBrokerPermission, isAccountApprovalLocked, isAccountPermissionDisabled, isAgentWithdrawalDisabled } from "../../../utils/permissionHelper";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import { fetchAgentClientAccounts } from "../../../controller/ibDashboard/getClientsController";
import Popup from "../../../components/Popup";
import { decryptResponseMessages, getHomePageInfos, getTradingAccounts, getTradingAccountInfos, updateTradingAccountInfos, resetMt5Password, createNewTradingAccount, createInternalTransferMoneyOperation, invalidateTradingAccountsCache } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { AccountType } from "../../../utils/enums";
import DemoAccountListController from "../../../controller/accountsDemo/demoAccountListController";
import { createDemoTradingAccount } from "../../../controller/accountsDemo/addDemoAccountController";
import { createDepositDemoOperation } from "../../../controller/accountsDemo/createDepositDemoController";
import UserTradingVolumeController from "../../../controller/accounts/userTradingVolumeController";
import DateRangeFilter from "../../../components/DateRangeFilter";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import { useToast } from "../../../contexts/ToastContext";
import { openSupportChatOnCurrentPage } from "../../../utils/supportChatNavigation";
import WalletController from "../../../controller/walletController";
import "../../css/List.css";
import "../../css/cachIn.css";
import { useBrandLogo } from "../../../utils/brandLogo";
import keyIcon from "../../../assets/images/key.png";

/**
 * When update-trading-infos returns TRADING_ENABLE (or nested aliases), use it for the edit UI.
 * @returns {boolean|null} null if the field is not present in the response
 */
const parseTradingEnabledFromUpdateResponse = (rawResponse) => {
  if (!rawResponse || typeof rawResponse !== "object") return null;

  const toBool = (value) => {
    if (value === 1 || value === true || value === "1") return true;
    if (value === 0 || value === false || value === "0") return false;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["true", "yes", "on", "enabled"].includes(normalized)) return true;
      if (["false", "no", "off", "disabled"].includes(normalized)) return false;
    }
    return null;
  };

  const tryBlock = (block) => {
    if (block == null || typeof block !== "object") return null;
    const keys = [
      "TRADING_ENABLE",
      "TRADING_ENABLED",
      "tradingEnable",
      "trading_enabled",
      "IS_TRADING_ENABLE",
    ];
    for (const k of keys) {
      if (!(k in block) || block[k] === undefined) continue;
      const b = toBool(block[k]);
      if (b !== null) return b;
    }
    const nested =
      block.account_information ||
      block.accountInformation ||
      block.ACCOUNT ||
      block.account ||
      null;
    if (nested && typeof nested === "object") {
      for (const k of keys) {
        if (!(k in nested) || nested[k] === undefined) continue;
        const b = toBool(nested[k]);
        if (b !== null) return b;
      }
    }
    return null;
  };

  const roots = [rawResponse.decryptedData, rawResponse.data].filter(Boolean);
  for (const root of roots) {
    const fromRoot = tryBlock(root);
    if (fromRoot !== null) return fromRoot;
    const data = root.DATA ?? root.data ?? root.BODY ?? root.body;
    if (data == null) continue;
    if (Array.isArray(data) && data.length > 0) {
      const fromArr = tryBlock(data[0]);
      if (fromArr !== null) return fromArr;
    } else if (typeof data === "object") {
      const fromData = tryBlock(data);
      if (fromData !== null) return fromData;
    }
  }
  return null;
};

const parseTradingEnabledValue = (value) => {
  if (value === 1 || value === true || value === "1") return true;
  if (value === 0 || value === false || value === "0") return false;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "yes", "on", "enabled"].includes(normalized)) return true;
    if (["false", "no", "off", "disabled"].includes(normalized)) return false;
  }
  return null;
};

const parseTradingEnabledFromAccount = (account, accountInfo) => {
  const keys = [
    "TRADING_ENABLE",
    "TRADING_ENABLED",
    "IS_TRADING_ENABLE",
    "IS_TRADING_ENABLED",
    "trading_enable",
    "trading_enabled",
  ];

  const readFrom = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    for (const key of keys) {
      if (!(key in obj) || obj[key] === undefined) continue;
      const parsed = parseTradingEnabledValue(obj[key]);
      if (parsed !== null) return parsed;
    }
    return null;
  };

  const direct = readFrom(accountInfo) ?? readFrom(account);
  if (direct !== null) return direct;

  const nested =
    accountInfo?.account_information ||
    accountInfo?.accountInformation ||
    account?.account_information ||
    account?.accountInformation ||
    null;
  const nestedParsed = readFrom(nested);
  if (nestedParsed !== null) return nestedParsed;

  return true;
};

const isNumericTradingGroupId = (value) => {
  if (value === null || value === undefined || value === "") return false;
  if (typeof value === "number" && Number.isFinite(value)) return true;
  const s = String(value).trim();
  return s !== "" && /^\d+$/.test(s);
};

const tradingGroupNameFromObject = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return "";
  const v =
    obj.GROUPE_NAME ??
    obj.groupe_name ??
    obj.GROUP_NAME ??
    obj.group_name ??
    obj.NAME ??
    obj.name ??
    obj.LABEL ??
    obj.label ??
    obj.TITLE ??
    obj.title;
  if (v == null || v === "") return "";
  return String(v).trim();
};

/**
 * Trading Group column: show group name from response, not raw TRADING_GROUP id.
 */
const resolveTradingGroupLabel = (account, accountInfo) => {
  const firstGroup = (raw) => {
    if (raw == null) return null;
    if (Array.isArray(raw)) return raw.length ? firstGroup(raw[0]) : null;
    return raw;
  };

  const groupObj = firstGroup(
    accountInfo.group ||
      accountInfo.Group ||
      accountInfo.GROUP ||
      accountInfo.trading_group ||
      account.group ||
      account.Group ||
      account.trading_group
  );

  if (groupObj != null && typeof groupObj === "object") {
    const fromObj = tradingGroupNameFromObject(groupObj);
    if (fromObj) return fromObj;
  }

  for (const block of [accountInfo, account]) {
    const tg = block?.TRADING_GROUP;
    if (tg != null && typeof tg === "object" && !Array.isArray(tg)) {
      const fromTg = tradingGroupNameFromObject(tg);
      if (fromTg) return fromTg;
    }
  }

  const nameFields = [
    accountInfo.TRADING_GROUP_NAME,
    accountInfo.TRADING_GROUP_LABEL,
    accountInfo.GROUP_NAME,
    accountInfo.group_name,
    account.TRADING_GROUP_NAME,
    account.TRADING_GROUP_LABEL,
    account.GROUP_NAME,
    account.group_name,
  ];
  for (const nf of nameFields) {
    if (nf == null || nf === "") continue;
    const s = String(nf).trim();
    if (s && !isNumericTradingGroupId(s)) return s;
  }

  const stringScalars = [
    accountInfo.group,
    account.group,
    accountInfo.GROUP,
    account.GROUP,
    accountInfo.trading_group,
    account.trading_group,
  ];
  for (const sv of stringScalars) {
    if (typeof sv !== "string") continue;
    const s = sv.trim();
    if (s && !isNumericTradingGroupId(s)) return s;
  }

  return "-";
};

const formatTradingGroupDisplay = (tradingGroup, t) => {
  const normalized = String(tradingGroup || "").trim().toLowerCase();
  if (normalized === "large") {
    return t("ib_tier_large", { ns: "mobile", defaultValue: "Large" });
  }
  if (normalized === "standard") {
    return t("ib_tier_standard", { ns: "mobile", defaultValue: "Standard" });
  }
  if (normalized === "mini") {
    return t("ib_tier_mini", { ns: "mobile", defaultValue: "Mini" });
  }
  return tradingGroup || "-";
};

const LEVERAGE_DROPDOWN_OPTIONS = [
  { value: "1:100", label: "1:100" },
  { value: "1:200", label: "1:200" },
];

const LOT_VOLUME_CHART_BARS = [
  { height: 32, accent: false },
  { height: 48, accent: false },
  { height: 28, accent: false },
  { height: 58, accent: false },
  { height: 44, accent: true },
  { height: 88, accent: true, glow: true },
  { height: 64, accent: true },
  { height: 36, accent: false },
];

const measureTextWidthPx = (text, font) => {
  if (typeof document === "undefined") {
    return String(text || "").length * 9;
  }
  if (!measureTextWidthPx._canvas) {
    measureTextWidthPx._canvas = document.createElement("canvas");
  }
  const ctx = measureTextWidthPx._canvas.getContext("2d");
  if (!ctx) return String(text || "").length * 9;
  ctx.font = font;
  return ctx.measureText(String(text || "")).width;
};

const AccountsList = ({
  defaultAccountType = "real",
  hideAccountTypeSwitcher = false,
  customTitle = null,
  ibAgentClientUserId = null,
  hideSummaryTransferWithdraw = false,
  hideSummaryDeposit = false,
  hideNewAccountButton = false,
  hideRowWithdraw = false,
  hideActionColumn = false,
  ibShowBackButton = false,
}) => {
  const isIbClientMode = Boolean(ibAgentClientUserId);
  const { t, i18n } = useTranslation();
  const brandLogo = useBrandLogo();
  const isRtl = i18n.dir() === "rtl";
  const normalizedCustomTitle = typeof customTitle === "string" ? customTitle.trim() : "";
  const resolvedCustomTitle =
    normalizedCustomTitle === "Real Accounts" || normalizedCustomTitle === "Real MT5 Accounts"
      ? t("real_accounts", { ns: "mobile" })
      : normalizedCustomTitle === "Demo Accounts"
      ? t("demo_accounts", { ns: "mobile" })
      : customTitle;
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, success: showSuccess, error: showError, warning: showWarning } = useToast();
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [globalLotVolume, setGlobalLotVolume] = useState(0);
  const [globalLotVolumeLoading, setGlobalLotVolumeLoading] = useState(false);
  const [volumeByAccount, setVolumeByAccount] = useState({});
  const [showAccountVolumeColumn, setShowAccountVolumeColumn] = useState(false);
  const [showVolumeDateFilter, setShowVolumeDateFilter] = useState(false);
  const [volumeRange, setVolumeRange] = useState({
    dateFrom: null,
    dateTo: null,
    presetId: "this_month",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10); // For real accounts
  const [demoPerPage] = useState(6); // For demo accounts (as per API requirement)
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [accountLimit, setAccountLimit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const searchContainerRef = useRef(null);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showResetPasswordPopup, setShowResetPasswordPopup] = useState(false);
  const [resetPasswordType, setResetPasswordType] = useState(null); // 0 = main, 1 = investor
  const [accountType, setAccountType] = useState(defaultAccountType === "demo" ? "demo" : "real");
  const [showAddAccountPopup, setShowAddAccountPopup] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [editForm, setEditForm] = useState({
    accountId: "",
    walletId: null,
    internalId: null,
    updateIds: [],
    initialName: "",
    initialLeverage: "",
    referalBroker: null,
    tradingGroupId: null,
    group: "",
    leverage: "",
    name: "",
    tradingEnabled: true,
    accountEnabled: true,
    isBroker: false,
    ableToEdit: true,
  });
  const [brokerGroups, setBrokerGroups] = useState([]);
  const [groupMode, setGroupMode] = useState(false);
  const [loadingBrokerGroups, setLoadingBrokerGroups] = useState(false);
  // Demo deposit popup state
  const [showDemoDepositPopup, setShowDemoDepositPopup] = useState(false);
  const [demoDepositAmount, setDemoDepositAmount] = useState("");
  const [demoDepositError, setDemoDepositError] = useState(null);
  const [demoDepositProcessing, setDemoDepositProcessing] = useState(false);
  const [demoDepositWalletId, setDemoDepositWalletId] = useState(null);
  const [demoDepositAccountId, setDemoDepositAccountId] = useState(null);
  const [demoDepositCurrencySymbol, setDemoDepositCurrencySymbol] = useState("");
  const [demoDepositTransactionId, setDemoDepositTransactionId] = useState(null);
  const [showDemoPasswordDialog, setShowDemoPasswordDialog] = useState(false);
  // Share / broker information popup state
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareBrokerId, setShareBrokerId] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  // Real internal-transfer deposit popup state (USD -> MT5 wallet)
  const [showRealDepositPopup, setShowRealDepositPopup] = useState(false);
  const [realDepositAmount, setRealDepositAmount] = useState("");
  const [realDepositError, setRealDepositError] = useState(null);
  const [realDepositProcessing, setRealDepositProcessing] = useState(false);
  const [realDepositWalletId, setRealDepositWalletId] = useState(null); // MT5 wallet ID (destination)
  const [realDepositUsdWalletId, setRealDepositUsdWalletId] = useState(null); // USD wallet ID (source)
  const [realDepositCurrencySymbol, setRealDepositCurrencySymbol] = useState("");
  const [realDepositAccountId, setRealDepositAccountId] = useState(null);
  const [showRealPasswordDialog, setShowRealPasswordDialog] = useState(false);
  const [realDepositTransactionId, setRealDepositTransactionId] = useState(null);
  // Real internal-transfer withdraw popup state (MT5 wallet -> USD)
  const [showRealWithdrawPopup, setShowRealWithdrawPopup] = useState(false);
  const [realWithdrawAmount, setRealWithdrawAmount] = useState("");
  const [realWithdrawError, setRealWithdrawError] = useState(null);
  const [realWithdrawProcessing, setRealWithdrawProcessing] = useState(false);
  const [realWithdrawWalletId, setRealWithdrawWalletId] = useState(null); // MT5 wallet ID (source)
  const [realWithdrawUsdWalletId, setRealWithdrawUsdWalletId] = useState(null); // USD wallet ID (destination)
  const [realWithdrawCurrencySymbol, setRealWithdrawCurrencySymbol] = useState("");
  const [realWithdrawAccountId, setRealWithdrawAccountId] = useState(null);
  const [showRealWithdrawPasswordDialog, setShowRealWithdrawPasswordDialog] = useState(false);
  const [realWithdrawTransactionId, setRealWithdrawTransactionId] = useState(null);

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const { customerPerm, customerDevicePerm, brokerPerm, brokerDevicePerm } = permissions;

  const canUpdateLeverageIb = useMemo(
    () =>
      hasBrokerPermission(
        brokerPerm,
        brokerDevicePerm,
        PermissionBrokerModelController.PER__CAN_UPDATE_LEVERAGE
      ),
    [brokerPerm, brokerDevicePerm]
  );
  const canEnableTradingIb = useMemo(
    () =>
      hasBrokerPermission(
        brokerPerm,
        brokerDevicePerm,
        PermissionBrokerModelController.PER__CAN_ENABLE_TRADING_ACCOUNT__TRADING
      ),
    [brokerPerm, brokerDevicePerm]
  );
  const approvalLocked = useMemo(() => isAccountApprovalLocked(user), [user]);
  
  // Check if any account has an agent (not "-", null, or undefined)
  const hasAgent = useMemo(() => {
    if (!accounts || accounts.length === 0) return false;
    return accounts.some(account => {
      const accountInfo = account.account_information || account.accountInformation || {};
      const agent = accountInfo.REFERAL_BROKER || account.REFERAL_BROKER || account.AGENT || account.agent || account.AGENT_ID || account.agent_id || null;
      return agent && agent !== "-" && agent !== null && agent !== undefined;
    });
  }, [accounts]);
  
  // Permission checks for action buttons
  const canEditAccount = !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__MANAGEMENT_REAL_ACCOUNT
    );
  const canDeposit = !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_USD_MT5
    );
  const canDepositDemo = !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_DEPOSIT_DEMO
    );
  // Hide broker-row withdraw only when AGENT_WITHDRAWAL_DISABLED is checked.
  const agentWithdrawalDisabled = useMemo(() => isAgentWithdrawalDisabled(user), [user]);
  const canWithdraw = !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_USD
    );
  // Permission for creating new Real Account (used by Add Account popup)
  const canAddRealAccount = !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__ADD_REAL_ACCOUNT
    );
  
  // Permission checks for edit popup fields
  const canUpdateLeverage = !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_UPDATE_LEVERAGE
    );
  const canEnableTrading = !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_ENABLE_TRADING_ACCOUNT__TRADING
    );
  const canViewAccountHistory = !approvalLocked &&
    !isAccountPermissionDisabled(
      customerPerm,
      PermissionCustomerModelController.PER__CAN_VIEW_ACCOUNT_HISTORY
    );
  const canViewTotalVolumeAccounts = !approvalLocked &&
    !isAccountPermissionDisabled(
      customerPerm,
      PermissionCustomerModelController.PER__CAN_VIEW_TOTAL_VOLUME_ACCOUNTS
    );
  const canViewClientTradingHistory = !isAccountPermissionDisabled(
    brokerPerm,
    PermissionBrokerModelController.PER__CAN_VIEW_CLIENT_TRADING_HISTORY
  );
  const showGlobalLotVolume = isIbClientMode
    ? canViewClientTradingHistory
    : canViewTotalVolumeAccounts;

  const canUpdateLeverageEffective = isIbClientMode ? canUpdateLeverageIb : canUpdateLeverage;
  const canEnableTradingEffective = isIbClientMode ? canEnableTradingIb : canEnableTrading;
  const canEditAccountRow = canEditAccount || isIbClientMode;
  // Flutter: isEditMode && groupMode && (agentView ? ableToEdit : true)
  const canEditTradingGroup =
    isIbClientMode &&
    groupMode &&
    (editForm.ableToEdit !== false);

  useEffect(() => {
    let cancelled = false;

    const applyUserData = (userData) => {
      if (!userData || cancelled) return;
      setUser(userData);
      const userInfo = userData.user_information || userData.userInformation || {};
      const limit =
        userInfo.ACCOUNT_LIMIT ||
        userInfo.accountLimit ||
        userData.ACCOUNT_LIMIT ||
        userData.accountLimit;
      if (limit !== null && limit !== undefined) {
        setAccountLimit(Number(limit));
      }
    };

    const loadUserData = async () => {
      try {
        const storedUser = sessionStorage.getItem("userData");
        const storedTs = sessionStorage.getItem("userDataTimestamp");
        const maxAgeMs = 30 * 60 * 1000;
        if (
          storedUser &&
          storedTs &&
          Date.now() - Number(storedTs) < maxAgeMs
        ) {
          applyUserData(JSON.parse(storedUser));
          return;
        }
      } catch (_) {
        // Fall through to home-page API.
      }

      try {
        // Reuse cached home-page payload first; this matches mobile behavior
        // where permissions are loaded once and reused across navigation.
        const userResponse = await getHomePageInfos();
        const decrypted = userResponse?.decryptedData || userResponse?.data || {};
        const freshUser = decrypted?.DATA || null;

        if (freshUser) {
          try {
            sessionStorage.setItem("userData", JSON.stringify(freshUser));
            sessionStorage.setItem("userDataTimestamp", Date.now().toString());
          } catch (_) {
            // ignore storage write failures
          }
          applyUserData(freshUser);
          return;
        }
      } catch (_) {
        // Fall back to cached session user data.
      }

      try {
        const storedUser = sessionStorage.getItem("userData");
        if (storedUser) {
          applyUserData(JSON.parse(storedUser));
        }
      } catch (_) {
        // ignore cached parse errors
      }
    };

    loadUserData();

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchAccounts = async (search = null, opts = {}) => {
    try {
      setLoading(true);
      setError(null);

      const searchValue = search !== null ? search : searchTerm;
      const pageForRequest = opts.page != null ? opts.page : currentPage;
      const forceRefresh = opts.forceRefresh === true;

      if (ibAgentClientUserId) {
        const response = await fetchAgentClientAccounts(ibAgentClientUserId, {
          forceRefresh,
        });
        let data = [];
        if (response) {
          if (Array.isArray(response.DATA)) {
            data = response.DATA;
          } else if (response.DATA && Array.isArray(response.DATA.data)) {
            data = response.DATA.data;
          } else if (Array.isArray(response)) {
            data = response;
          } else if (Array.isArray(response.data)) {
            data = response.data;
          } else if (response.DATA && !Array.isArray(response.DATA)) {
            data = [response.DATA];
          }
        }
        const q = String(searchValue || "").trim().toLowerCase();
        let list = data;
        if (q) {
          list = data.filter((acc) => {
            const info = acc.account_information || acc.accountInformation || {};
            const tid = String(
              info.TRADING_ID || acc.TRADING_ID || acc.trading_id || ""
            ).toLowerCase();
            const name = String(info.NAME || acc.NAME || acc.name || "").toLowerCase();
            return tid.includes(q) || name.includes(q);
          });
        }
        setAccounts(list);
        setTotalAccounts(list.length);
        setLoading(false);
        return;
      }
      
      // Use demo controller for demo accounts, regular API for real accounts
      if (accountType === "demo") {
        const result = await DemoAccountListController.getDemoAccounts(demoPerPage, pageForRequest, searchValue);
        if (result.error) {
          setError("Failed to load demo accounts");
          setAccounts([]);
          setTotalAccounts(0);
          setAccountLimit(null);
        } else {
          setAccounts(result.accounts);
          setTotalAccounts(result.total);
          // ACCOUNT_LIMIT comes from dashboard API, not from demo accounts API
          // It's already set from userData in useEffect
        }
        setLoading(false);
        return;
      }
      
      // Real accounts
      const response = await getTradingAccounts(0, perPage, pageForRequest, searchValue, {
        forceRefresh,
      });

      // Decrypt the response
      let decryptedData = null;

      if (response.decryptedData) {
        decryptedData = response.decryptedData;
      } else if (response.data) {
        decryptedData = (await decryptResponseMessages(response.data)) || response.data;
      }


      // Extract accounts data from response
      let accountsData = [];
      let total = 0;

      if (decryptedData) {
        // ACCOUNT_LIMIT comes from dashboard API, not from trading accounts API
        // So we don't extract it here - it's already set from userData in useEffect
        
        // Check various possible response structures
        if (decryptedData.DATA && Array.isArray(decryptedData.DATA)) {
          accountsData = decryptedData.DATA;
          total = decryptedData.TOTAL || decryptedData.total || accountsData.length;
        } else if (decryptedData.DATA && !Array.isArray(decryptedData.DATA)) {
          accountsData = [decryptedData.DATA];
          total = decryptedData.TOTAL || decryptedData.total || 1;
        } else if (Array.isArray(decryptedData)) {
          accountsData = decryptedData;
          total = decryptedData.length;
        } else if (decryptedData.accounts && Array.isArray(decryptedData.accounts)) {
          accountsData = decryptedData.accounts;
          total = decryptedData.total || decryptedData.TOTAL || accountsData.length;
        } else if (decryptedData.data && Array.isArray(decryptedData.data)) {
          accountsData = decryptedData.data;
          total = decryptedData.total || decryptedData.TOTAL || accountsData.length;
        }
      }


      setAccounts(accountsData);
      setTotalAccounts(total || accountsData.length);

    } catch (err) {
      setError("Failed to load trading accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, accountType, ibAgentClientUserId]);

  useEffect(() => {
    let cancelled = false;
    const hasRange = Boolean(volumeRange.dateFrom && volumeRange.dateTo);

    if (!showGlobalLotVolume || !hasRange) {
      setGlobalLotVolume(0);
      setVolumeByAccount({});
      setShowAccountVolumeColumn(false);
      setGlobalLotVolumeLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setGlobalLotVolumeLoading(true);

    const loadGlobalLotVolume = async () => {
      try {
        const summary = await UserTradingVolumeController.getVolumeSummary({
          accountType: accountType === "demo" ? AccountType.DEMO : AccountType.REAL,
          userId: ibAgentClientUserId || null,
          dateFrom: volumeRange.dateFrom,
          dateTo: volumeRange.dateTo,
        });
        if (!cancelled) {
          setGlobalLotVolume(summary.total);
          setVolumeByAccount(summary.byKey || {});
        }
      } catch (_) {
        if (!cancelled) {
          setGlobalLotVolume(0);
          setVolumeByAccount({});
        }
      } finally {
        if (!cancelled) {
          setGlobalLotVolumeLoading(false);
        }
      }
    };

    loadGlobalLotVolume();
    return () => {
      cancelled = true;
    };
  }, [
    accountType,
    ibAgentClientUserId,
    showGlobalLotVolume,
    volumeRange.dateFrom,
    volumeRange.dateTo,
  ]);

  useEffect(() => {
    setShowAccountVolumeColumn(false);
    setShowVolumeDateFilter(false);
    setVolumeRange({
      dateFrom: null,
      dateTo: null,
      presetId: "this_month",
    });
  }, [accountType, ibAgentClientUserId]);

  useEffect(() => {
    const onWalletRefresh = () => {
      invalidateTradingAccountsCache();
      fetchAccounts(searchTerm, { forceRefresh: true });
    };
    window.addEventListener("mfg:funds-refresh", onWalletRefresh);
    window.addEventListener("dashboard:refresh", onWalletRefresh);
    return () => {
      window.removeEventListener("mfg:funds-refresh", onWalletRefresh);
      window.removeEventListener("dashboard:refresh", onWalletRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Handle click outside search container — collapse search UI only; keep active filter
  // so row actions (edit, deposit, etc.) do not reset the table.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSearchActive && searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        // Check if click is not on the search icon button
        const searchIconBtn = event.target.closest('.action-icon-btn');
        if (!searchIconBtn) {
          setIsSearchActive(false);
        }
      }
    };

    if (isSearchActive) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchActive]);

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
    fetchAccounts(searchTerm);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
    fetchAccounts("");
    setIsSearchActive(false);
  };

  const handleToggleSearch = () => {
    setIsSearchActive(!isSearchActive);
    if (isSearchActive) {
      // If closing search, clear it
      handleClearSearch();
    }
  };

  const handleGoToAddPage = () => {
    // Block creating any new account when profile is incomplete
    // or account approval is still pending.
    if (approvalLocked) {
      if (showWarning) {
        showWarning(
          "Account locked",
          "You must complete your profile and wait for account approval before creating new accounts."
        );
      }
      return;
    }

    // Check if account limit is exceeded (skip for demo accounts)
    if (accountType !== "demo" && accountLimit !== null && accountLimit !== undefined && (totalAccounts || accounts.length) >= accountLimit) {
      showToast(
        "contact-admin",
        "Account Limit Exceeded",
        `You have reached your account limit of ${accountLimit}. Please contact admin.`
      );
      return;
    }
    // Show popup instead of navigating
    setShowAddAccountPopup(true);
  };

  const handleCloseAddAccountPopup = () => {
    setShowAddAccountPopup(false);
  };

  const handleConfirmAddAccount = async () => {
    if (creatingAccount) return;

    // Real account creation still requires permission
    if (accountType === "real" && !canAddRealAccount) return;

    setCreatingAccount(true);
    setError(null);
    try {
      if (accountType === "demo") {
        // Create DEMO trading account (ACCOUNT_TYPE: 2)
        await createDemoTradingAccount();
      } else {
        // ACCOUNT_TYPE: 0 -> Real account
        const response = await createNewTradingAccount(0);
        const data = response?.decryptedData || response?.data || {};
        const code = Number(
          data?.CODE ??
          data?.code ??
          data?.BODY?.CODE ??
          data?.body?.CODE ??
          data?.body?.code
        );

        if (code !== AppConstants.responseOk) {
          const message =
            data?.MESSAGE ||
            data?.message ||
            data?.BODY?.MESSAGE ||
            data?.body?.MESSAGE ||
            data?.body?.message ||
            "Failed to create new Real Account. Please try again.";
          setError(message);
          if (showError) {
            showError("Account creation failed", message);
          }
          return;
        }
      }
      setShowAddAccountPopup(false);
      setCurrentPage(1);
      invalidateTradingAccountsCache();
      // Refetch page 1 immediately (state currentPage may not have flushed yet)
      await fetchAccounts(null, { page: 1 });
    } catch (err) {
      const fallbackMsg =
        accountType === "demo"
          ? "Failed to create new Demo Account. Please try again."
          : err?.message || "Failed to create new Real Account. Please try again.";
      setError(fallbackMsg);
      if (showError) {
        showError("Account creation failed", fallbackMsg);
      }
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleOpenEditPopup = async (data) => {
    // Normalize leverage to dropdown format (e.g. "1:100" / "1:200")
    let leverageValue = "";
    if (data.leverage != null) {
      const raw = String(data.leverage);
      if (raw.includes(":")) {
        leverageValue = raw;
      } else if (raw.includes("/")) {
        const parts = raw.split("/");
        leverageValue = parts.length === 2 ? `${parts[0]}:${parts[1]}` : raw;
      } else {
        leverageValue = `1:${raw}`;
      }
    }

    const initialTradingGroupId = data.tradingGroupId ?? null;
    const initialGroupLabel = data.group || "";

    setEditForm({
      accountId: data.tradingId || "",
      walletId: data.walletId ?? null,
      internalId: data.internalId ?? null,
      updateIds: Array.isArray(data.updateIds) ? data.updateIds : [],
      initialName: data.name || "",
      initialLeverage: leverageValue,
      referalBroker: data.referalBroker ?? null,
      tradingGroupId: initialTradingGroupId,
      group: initialGroupLabel,
      leverage: leverageValue,
      name: data.name || "",
      tradingEnabled: data.tradingEnabled !== undefined ? data.tradingEnabled : true,
      accountEnabled: data.accountEnabled !== undefined ? data.accountEnabled : true,
      isBroker: data.isBroker ?? false,
      ableToEdit: true,
    });
    setBrokerGroups([]);
    setGroupMode(false);
    setIsEditMode(false);
    setShowEditPopup(true);

    // Agent/IB view: load assignable broker groups (Flutter get-trading-account-infos + AGENT_VIEW)
    if (isIbClientMode && data.internalId != null && data.internalId !== "") {
      setLoadingBrokerGroups(true);
      try {
        const response = await getTradingAccountInfos(data.internalId, true);
        const decrypted = response?.decryptedData ?? response?.data ?? {};
        const code = Number(decrypted?.CODE ?? decrypted?.code ?? 0);

        if (code === AppConstants.responseOk || code === 200) {
          const payload = decrypted?.DATA || {};
          const accountPayload = payload.ACCOUNT || {};
          const accountInfo =
            accountPayload.account_information ||
            accountPayload.accountInformation ||
            {};
          const ableToEditRaw =
            accountInfo.ABLE_TO_EDIT ??
            accountInfo.ableToEdit ??
            accountPayload.ABLE_TO_EDIT;
          const ableToEdit =
            ableToEditRaw === undefined || ableToEditRaw === null
              ? true
              : ableToEditRaw === 1 ||
                ableToEditRaw === true ||
                ableToEditRaw === "1";

          const currentGroupId =
            accountInfo.TRADING_GROUP ??
            accountInfo.tradingGroup ??
            initialTradingGroupId;
          const currentGroupObj =
            accountInfo.group ||
            accountInfo.Group ||
            accountPayload.group ||
            null;
          const currentGroupName =
            currentGroupObj?.GROUPE_NAME ||
            currentGroupObj?.groupeName ||
            currentGroupObj?.NAME ||
            currentGroupObj?.name ||
            initialGroupLabel;

          const rawGroups = Array.isArray(payload.BROKER_GROUPS)
            ? payload.BROKER_GROUPS
            : [];
          const mappedGroups = rawGroups
            .map((g) => ({
              id: g?.GROUPE_ID ?? g?.groupe_id ?? g?.ID ?? g?.id ?? null,
              name:
                g?.GROUPE_NAME ??
                g?.groupe_name ??
                g?.groupeName ??
                g?.NAME ??
                g?.name ??
                "",
            }))
            .filter((g) => g.id != null);

          // Flutter updateBrokerGroups(): if current group is not in broker list,
          // fall back to read-only current group only.
          const selectedIdStr =
            currentGroupId != null && currentGroupId !== ""
              ? String(currentGroupId)
              : null;
          const currentInBrokerList =
            selectedIdStr != null &&
            mappedGroups.some((g) => String(g.id) === selectedIdStr);

          let nextGroups = mappedGroups;
          let nextGroupMode = mappedGroups.length > 0;

          if (rawGroups.length > 0 && !currentInBrokerList && selectedIdStr) {
            nextGroupMode = false;
            nextGroups = [
              {
                id: currentGroupId,
                name: currentGroupName || String(currentGroupId),
              },
            ];
          } else if (rawGroups.length === 0 && selectedIdStr) {
            nextGroupMode = false;
            nextGroups = [
              {
                id: currentGroupId,
                name: currentGroupName || String(currentGroupId),
              },
            ];
          }

          const selectedGroup = nextGroups.find(
            (g) => String(g.id) === selectedIdStr
          );

          const walletsRaw =
            accountPayload.wallet ?? accountPayload.wallets ?? null;
          const wallets = Array.isArray(walletsRaw)
            ? walletsRaw
            : walletsRaw && typeof walletsRaw === "object"
              ? [walletsRaw]
              : [];
          const firstWallet = wallets[0] || {};
          const walletIdFromApi =
            firstWallet.ID ?? firstWallet.id ?? firstWallet.WALLET_ID ?? null;

          const accountEnabledRaw =
            accountPayload.IS_ACCOUNT_ENABLE ??
            accountPayload.isAccountEnable ??
            accountPayload.is_account_enable;
          const accountEnabled =
            accountEnabledRaw === undefined || accountEnabledRaw === null
              ? true
              : accountEnabledRaw === 1 ||
                accountEnabledRaw === true ||
                accountEnabledRaw === "1";

          const tradingEnabledRaw =
            accountInfo.IS_TRADING_ENABLE ??
            accountInfo.isTradingEnable ??
            accountInfo.is_trading_enable;
          const tradingEnabledFromApi =
            tradingEnabledRaw === undefined || tradingEnabledRaw === null
              ? null
              : tradingEnabledRaw === 1 ||
                tradingEnabledRaw === true ||
                tradingEnabledRaw === "1";

          const leverageFromApi = accountInfo.LEVERAGE ?? accountInfo.leverage;
          let leverageValueFromApi = null;
          if (leverageFromApi != null && String(leverageFromApi).trim() !== "") {
            const rawLev = String(leverageFromApi);
            if (rawLev.includes(":")) leverageValueFromApi = rawLev;
            else if (rawLev.includes("/")) {
              const parts = rawLev.split("/");
              leverageValueFromApi =
                parts.length === 2 ? `${parts[0]}:${parts[1]}` : rawLev;
            } else {
              leverageValueFromApi = `1:${rawLev}`;
            }
          }

          const nameFromApi =
            accountInfo.NAME ?? accountInfo.name ?? null;
          const referalFromApi =
            accountInfo.REFERAL_BROKER ??
            accountInfo.referalBroker ??
            null;

          setBrokerGroups(nextGroups);
          setGroupMode(nextGroupMode);
          setEditForm((prev) => ({
            ...prev,
            ableToEdit,
            // Flutter update uses wallets.first.id — prefer API wallet id.
            walletId: walletIdFromApi ?? prev.walletId,
            tradingGroupId: (() => {
              const raw = currentGroupId ?? prev.tradingGroupId;
              if (raw == null || raw === "") return prev.tradingGroupId;
              if (typeof raw === "object") {
                const id =
                  raw.GROUPE_ID ?? raw.groupe_id ?? raw.ID ?? raw.id ?? null;
                return isNumericTradingGroupId(id) ? Number(id) : id;
              }
              return isNumericTradingGroupId(raw) ? Number(raw) : raw;
            })(),
            group: selectedGroup?.name || currentGroupName || prev.group,
            accountEnabled,
            tradingEnabled:
              tradingEnabledFromApi !== null
                ? tradingEnabledFromApi
                : prev.tradingEnabled,
            name:
              nameFromApi != null && String(nameFromApi).trim() !== ""
                ? String(nameFromApi)
                : prev.name,
            initialName:
              nameFromApi != null && String(nameFromApi).trim() !== ""
                ? String(nameFromApi)
                : prev.initialName,
            leverage: leverageValueFromApi || prev.leverage,
            initialLeverage: leverageValueFromApi || prev.initialLeverage,
            referalBroker: referalFromApi ?? prev.referalBroker,
          }));
        }
      } catch (_) {
        // Keep read-only current group label if group fetch fails.
        setBrokerGroups(
          initialTradingGroupId != null
            ? [{ id: initialTradingGroupId, name: initialGroupLabel || String(initialTradingGroupId) }]
            : []
        );
        setGroupMode(false);
      } finally {
        setLoadingBrokerGroups(false);
      }
    }
  };

  const handleCloseEditPopup = () => {
    setShowEditPopup(false);
    setIsEditMode(false);
    setBrokerGroups([]);
    setGroupMode(false);
    setLoadingBrokerGroups(false);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggleTradingEnabled = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!isEditMode || !canEnableTradingEffective) return;
    setEditForm((prev) => ({
      ...prev,
      tradingEnabled: !prev.tradingEnabled,
    }));
  };

  const handleOpenResetPasswordPopup = (passwordType) => {
    // Use internalId (ACCOUNT_ID in backend) for reset MT5 password
    if (!editForm.internalId) return;
    // Close the edit popup when opening the reset password confirmation popup
    setShowEditPopup(false);
    setIsEditMode(false);
    setResetPasswordType(passwordType);
    setShowResetPasswordPopup(true);
  };

  const handleCloseResetPasswordPopup = () => {
    setShowResetPasswordPopup(false);
    setResetPasswordType(null);
  };

  const goToInternalTransfer = (transferMode, walletId = null) => {
    navigate(
      {
        pathname: "/pages/transfer",
        search: `?mode=${encodeURIComponent(transferMode)}`,
      },
      {
        state: {
          transferMode,
          ...(walletId != null ? { walletId } : {}),
        },
      }
    );
  };

  const handleConfirmResetPassword = async () => {
    // Backend expects ACCOUNT_ID; we stored it in internalId
    if (!editForm.internalId || resetPasswordType === null) {
      handleCloseResetPasswordPopup();
      return;
    }

    try {
      await resetMt5Password(editForm.internalId, resetPasswordType);
    } catch (error) {
      // Failed to reset MT5 password
    } finally {
      handleCloseResetPasswordPopup();
    }
  };

  const handleOpenDemoDepositPopup = (wallet, account) => {
    if (!wallet) return;

    const walletId = wallet.ID || wallet.id || wallet.WALLET_ID || null;
    if (!walletId) return;

    const accountInfo = account.account_information || account.accountInformation || {};
    const tradingId =
      accountInfo.TRADING_ID ||
      account.TRADING_ID ||
      account.trading_id ||
      account.ID ||
      account.id ||
      account.ACCOUNT_ID ||
      "-";

    const currencyInfo = wallet.currencies || {};
    const currencySymbol =
      currencyInfo.CURRENCY_SYMBOL ||
      wallet.CURRENCY_SYMBOL ||
      "";

    setDemoDepositWalletId(walletId);
    setDemoDepositAccountId(tradingId);
    setDemoDepositCurrencySymbol(currencySymbol);
    setDemoDepositAmount("");
    setDemoDepositError(null);
    setShowDemoDepositPopup(true);
  };

  const handleOpenRealDepositPopup = async (wallet, account) => {
    if (!wallet || !account) return;

    const toWalletId = wallet.ID || wallet.id || wallet.WALLET_ID || null;
    if (!toWalletId) return;

    try {
      setRealDepositProcessing(true);
      setRealDepositError(null);

      // Load wallets (from dashboard/session cache) and find USD / FIAT wallet
      const walletData = await WalletController.getWallets(false);
      const wallets = walletData?.wallets || [];

      let usdWallet = null;
      for (const w of wallets) {
        const currencyType =
          w.currencies?.TYPE ??
          w.currency?.type ??
          w.CURRENCY_TYPE ??
          w.currencyType ??
          null;

        const currencyCode =
          w.currencies?.CURRENCY_CODE ||
          w.currency?.currencyCode ||
          w.CURRENCY_CODE ||
          "";

        const currencySymbol =
          w.currencies?.CURRENCY_SYMBOL ||
          w.currency?.currencySymbol ||
          w.CURRENCY_SYMBOL ||
          "";

        const isFiatType = currencyType === 0;
        const isUsdByCode =
          String(currencyCode).toUpperCase() === "USD" ||
          String(currencySymbol) === "$";

        if (isFiatType || isUsdByCode) {
          usdWallet = { wallet: w, currencySymbol: currencySymbol || currencyCode || "USD" };
          break;
        }
      }

      if (!usdWallet) {
        if (showError) {
          showError(
            "Error Occurred",
            "USD wallet not found. You cannot deposit from USD to this trading account."
          );
        }
        setRealDepositProcessing(false);
        return;
      }

      const usdWalletId = usdWallet.wallet.ID || usdWallet.wallet.id || usdWallet.wallet.WALLET_ID || null;
      if (!usdWalletId) {
        if (showError) {
          showError(
            "Error Occurred",
            "USD wallet ID is missing. Please try again later."
          );
        }
        setRealDepositProcessing(false);
        return;
      }

      const accountInfo = account.account_information || account.accountInformation || {};
      const tradingId =
        accountInfo.TRADING_ID ||
        account.TRADING_ID ||
        account.trading_id ||
        account.ID ||
        account.id ||
        account.ACCOUNT_ID ||
        "-";

      setRealDepositWalletId(toWalletId);
      setRealDepositUsdWalletId(usdWalletId);
      setRealDepositCurrencySymbol(usdWallet.currencySymbol || "");
      setRealDepositAccountId(tradingId);
      setRealDepositAmount("");
      setRealDepositError(null);
      setShowRealDepositPopup(true);
    } catch (err) {
      if (showError) {
        showError(
          "Error Occurred",
          err?.message || "Failed to prepare internal transfer. Please try again."
        );
      }
    } finally {
      setRealDepositProcessing(false);
    }
  };

  const handleOpenRealWithdrawPopup = async (wallet, account) => {
    if (!wallet || !account) return;

    const fromWalletId = wallet.ID || wallet.id || wallet.WALLET_ID || null;
    if (!fromWalletId) return;

    try {
      setRealWithdrawProcessing(true);
      setRealWithdrawError(null);

      const walletData = await WalletController.getWallets(false);
      const wallets = walletData?.wallets || [];

      let usdWallet = null;
      for (const w of wallets) {
        const currencyType =
          w.currencies?.TYPE ??
          w.currency?.type ??
          w.CURRENCY_TYPE ??
          w.currencyType ??
          null;

        const currencyCode =
          w.currencies?.CURRENCY_CODE ||
          w.currency?.currencyCode ||
          w.CURRENCY_CODE ||
          "";

        const currencySymbol =
          w.currencies?.CURRENCY_SYMBOL ||
          w.currency?.currencySymbol ||
          w.CURRENCY_SYMBOL ||
          "";

        const isFiatType = currencyType === 0;
        const isUsdByCode =
          String(currencyCode).toUpperCase() === "USD" ||
          String(currencySymbol) === "$";

        if (isFiatType || isUsdByCode) {
          usdWallet = { wallet: w, currencySymbol: currencySymbol || currencyCode || "USD" };
          break;
        }
      }

      if (!usdWallet) {
        if (showError) {
          showError(
            "Error Occurred",
            "USD wallet not found. You cannot withdraw from this trading account."
          );
        }
        setRealWithdrawProcessing(false);
        return;
      }

      const usdWalletId = usdWallet.wallet.ID || usdWallet.wallet.id || usdWallet.wallet.WALLET_ID || null;
      if (!usdWalletId) {
        if (showError) {
          showError(
            "Error Occurred",
            "USD wallet ID is missing. Please try again later."
          );
        }
        setRealWithdrawProcessing(false);
        return;
      }

      const accountInfo = account.account_information || account.accountInformation || {};
      const tradingId =
        accountInfo.TRADING_ID ||
        account.TRADING_ID ||
        account.trading_id ||
        account.ID ||
        account.id ||
        account.ACCOUNT_ID ||
        "-";

      setRealWithdrawWalletId(fromWalletId);
      setRealWithdrawUsdWalletId(usdWalletId);
      setRealWithdrawCurrencySymbol(usdWallet.currencySymbol || "");
      setRealWithdrawAccountId(tradingId);
      setRealWithdrawAmount("");
      setRealWithdrawError(null);
      setShowRealWithdrawPopup(true);
    } catch (err) {
      if (showError) {
        showError(
          "Error Occurred",
          err?.message || "Failed to prepare internal transfer. Please try again."
        );
      }
    } finally {
      setRealWithdrawProcessing(false);
    }
  };

  const handleOpenSharePopup = (tradingId) => {
    if (!tradingId) return;
    const origin =
      typeof window !== "undefined" && window.location ? window.location.origin : "";
    const path = `/informationAccount/${encodeURIComponent(tradingId)}`;
    setShareBrokerId(tradingId);
    setShareUrl(`${origin}${path}`);
    setShowSharePopup(true);
  };

  const handleCloseSharePopup = () => {
    setShowSharePopup(false);
    setShareBrokerId(null);
    setShareUrl("");
  };

  const handleConfirmSharePopup = () => {
    handleCloseSharePopup();
  };

  const handleCopyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      }
      if (showSuccess) {
        showSuccess("Link copied", "The account information link has been copied to your clipboard.");
      }
    } catch (_) {
      if (showError) {
        showError("Error Occurred", "Failed to copy link to clipboard");
      }
    }
  };

  const handleShareWhatsApp = async () => {
    if (!shareUrl) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      }
    } catch (_) {
      // Still open WhatsApp even if clipboard write fails.
    }
    const waUrl = `https://wa.me/?text=${encodeURIComponent(shareUrl)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  const handleCloseDemoDepositPopup = () => {
    if (demoDepositProcessing) return;
    setShowDemoDepositPopup(false);
    setDemoDepositAmount("");
    setDemoDepositError(null);
  };

  const handleCloseRealDepositPopup = () => {
    if (realDepositProcessing) return;
    setShowRealDepositPopup(false);
    setRealDepositAmount("");
    setRealDepositError(null);
    setRealDepositWalletId(null);
    setRealDepositUsdWalletId(null);
    setRealDepositAccountId(null);
  };

  const handleCloseRealWithdrawPopup = () => {
    if (realWithdrawProcessing) return;
    setShowRealWithdrawPopup(false);
    setRealWithdrawAmount("");
    setRealWithdrawError(null);
    setRealWithdrawWalletId(null);
    setRealWithdrawUsdWalletId(null);
    setRealWithdrawAccountId(null);
  };

  const handleConfirmDemoDeposit = async () => {
    if (demoDepositProcessing) return;

    const value = (demoDepositAmount || "").trim();
    if (!value) {
      setDemoDepositError("Amount is required");
      return;
    }

    const amountNumber = parseFloat(value);
    if (Number.isNaN(amountNumber) || amountNumber <= 0) {
      setDemoDepositError("Amount must be a positive number");
      return;
    }

    if (!demoDepositWalletId) {
      setDemoDepositError("Wallet not found for this demo account.");
      return;
    }

    setDemoDepositProcessing(true);
    setDemoDepositError(null);

    try {
      const res = await createDepositDemoOperation(amountNumber, demoDepositWalletId);
      
      // Check for HTTP error status first
      if (res.status && res.status >= 400) {
        const errorMsg = res.decryptedData?.MESSAGE || 
                        res.decryptedData?.message || 
                        res.decryptedData?.ERROR ||
                        res.data?.MESSAGE || 
                        res.data?.message ||
                        res.data?.ERROR ||
                        `Server error (${res.status})`;
        console.error('Demo deposit HTTP error:', res.status, res.decryptedData || res.data);
        throw new Error(errorMsg);
      }
      
      const d = res.decryptedData || res.data || {};
      const code = Number(d.CODE ?? d.code ?? 0);

      if (code && code !== 200 && code !== AppConstants.responseOk) {
        const errorMsg = d.MESSAGE || d.message || d.ERROR || "Failed to create demo deposit operation.";
        console.error('Demo deposit error code:', code, d);
        throw new Error(errorMsg);
      }

      const txId =
        d.TRANSACTION_ID ||
        d.transactionId ||
        d.REQUEST_TOKEN ||
        d.requestToken ||
        null;

      if (!txId) {
        throw new Error("TRANSACTION_ID is missing in server response.");
      }

      setDemoDepositTransactionId(txId);
      setShowDemoDepositPopup(false);
      setShowDemoPasswordDialog(true);
    } catch (err) {
      console.error('Demo deposit catch error:', err);
      console.error('Demo deposit error message:', err.message);
      console.error('Demo deposit error stack:', err.stack);
      const errorMessage = err.message || "Failed to create demo deposit.";
      if (showError) {
        showError("Error Occurred", errorMessage);
      } else {
        setDemoDepositError(errorMessage);
      }
    } finally {
      setDemoDepositProcessing(false);
    }
  };

  const handleConfirmRealDeposit = async () => {
    if (realDepositProcessing) return;

    const value = (realDepositAmount || "").trim();
    if (!value) {
      setRealDepositError("Amount is required");
      return;
    }

    const amountNumber = parseFloat(value);
    if (Number.isNaN(amountNumber) || amountNumber <= 0) {
      setRealDepositError("Amount must be a positive number");
      return;
    }

    if (!realDepositUsdWalletId || !realDepositWalletId) {
      setRealDepositError("Source or destination wallet not found.");
      return;
    }

    setRealDepositProcessing(true);
    setRealDepositError(null);

    try {
      const res = await createInternalTransferMoneyOperation({
        amount: amountNumber,
        fromWalletId: realDepositUsdWalletId,
        toWalletId: realDepositWalletId,
      });

      if (res.status && res.status >= 400) {
        const d = res.decryptedData || res.data || {};
        const errorMsg =
          d.MESSAGE ||
          d.message ||
          d.ERROR ||
          `Server error (${res.status})`;
        throw new Error(errorMsg);
      }

      const d = res.decryptedData || res.data || {};
      const code = Number(d.CODE ?? d.code ?? 0);

      if (code && code !== 200 && code !== AppConstants.responseOk) {
        const errorMsg =
          d.MESSAGE || d.message || d.ERROR || "Failed to create internal transfer operation.";
        throw new Error(errorMsg);
      }

      const txId =
        d.TRANSACTION_ID ||
        d.transactionId ||
        d.REQUEST_TOKEN ||
        d.requestToken ||
        null;

      if (!txId) {
        throw new Error("TRANSACTION_ID is missing in server response.");
      }

      setRealDepositTransactionId(txId);
      setShowRealDepositPopup(false);
      setShowRealPasswordDialog(true);
    } catch (err) {
      const errorMessage = err.message || "Failed to create internal transfer.";
      if (showError) {
        showError("Error Occurred", errorMessage);
      } else {
        setRealDepositError(errorMessage);
      }
    } finally {
      setRealDepositProcessing(false);
    }
  };

  const handleDemoPasswordDialogClose = async (result, data = {}) => {
    setShowDemoPasswordDialog(false);
    setDemoDepositTransactionId(null);

    if (result === "ok" || result === "need_admin") {
      if (showSuccess) {
        showSuccess(
          result === "ok" ? "Demo deposit submitted" : "Demo deposit pending",
          result === "ok"
            ? "Your demo balance will be updated shortly."
            : "Demo deposit is pending admin approval."
        );
      }
      try {
        await fetchAccounts();
      } catch (_) {
        // ignore refresh errors
      }
    } else if (result === "canceled") {
      if (showWarning) {
        showWarning("Action canceled", "Demo deposit has been canceled.");
      }
    } else if (result === "failed") {
      if (showError) {
        const errorMessage = data.error?.response?.data?.MESSAGE || 
                            data.error?.response?.data?.message ||
                            data.responseData?.MESSAGE ||
                            data.responseData?.message ||
                            data.error?.message ||
                            "Demo deposit failed. Please try again.";
        showError("Error Occurred", errorMessage);
      }
    }

    // Always refresh accounts to keep wallet balances up to date
    if (result !== "ok" && result !== "need_admin") {
      try { await fetchAccounts(); } catch (_) {}
    }
  };

  const handleRealPasswordDialogClose = async (result, data = {}) => {
    setShowRealPasswordDialog(false);
    setRealDepositTransactionId(null);

    if (result === "ok" || result === "need_admin") {
      if (showSuccess) {
        showSuccess(
          result === "ok" ? "Deposit submitted" : "Deposit pending",
          result === "ok"
            ? "Your account balance will be updated shortly."
            : "Deposit is pending admin approval."
        );
      }
      try {
        await fetchAccounts();
      } catch (_) {
        // ignore refresh errors
      }
    } else if (result === "canceled") {
      if (showWarning) {
        showWarning("Action canceled", "Deposit has been canceled.");
      }
    } else if (result === "failed") {
      if (showError) {
        const errorMessage =
          data.error?.response?.data?.MESSAGE ||
          data.error?.response?.data?.message ||
          data.responseData?.MESSAGE ||
          data.responseData?.message ||
          data.error?.message ||
          "Deposit failed. Please try again.";
        showError("Error Occurred", errorMessage);
      }
    }

    // Always refresh accounts to keep wallet balances up to date
    if (result !== "ok" && result !== "need_admin") {
      try { await fetchAccounts(); } catch (_) {}
    }
  };

  const handleRealWithdrawConfirm = async () => {
    if (realWithdrawProcessing) return;

    const value = (realWithdrawAmount || "").trim();
    if (!value) {
      setRealWithdrawError("Amount is required");
      return;
    }

    const amountNumber = parseFloat(value);
    if (Number.isNaN(amountNumber) || amountNumber <= 0) {
      setRealWithdrawError("Amount must be a positive number");
      return;
    }

    if (!realWithdrawWalletId || !realWithdrawUsdWalletId) {
      setRealWithdrawError("Source or destination wallet not found.");
      return;
    }

    setRealWithdrawProcessing(true);
    setRealWithdrawError(null);

    try {
      const res = await createInternalTransferMoneyOperation({
        amount: amountNumber,
        fromWalletId: realWithdrawWalletId,
        toWalletId: realWithdrawUsdWalletId,
      });

      if (res.status && res.status >= 400) {
        const d = res.decryptedData || res.data || {};
        const errorMsg =
          d.MESSAGE ||
          d.message ||
          d.ERROR ||
          `Server error (${res.status})`;
        throw new Error(errorMsg);
      }

      const d = res.decryptedData || res.data || {};
      const code = Number(d.CODE ?? d.code ?? 0);

      if (code && code !== 200 && code !== AppConstants.responseOk) {
        const errorMsg =
          d.MESSAGE || d.message || d.ERROR || "Failed to create internal transfer operation.";
        throw new Error(errorMsg);
      }

      const txId =
        d.TRANSACTION_ID ||
        d.transactionId ||
        d.REQUEST_TOKEN ||
        d.requestToken ||
        null;

      if (!txId) {
        throw new Error("TRANSACTION_ID is missing in server response.");
      }

      setRealWithdrawTransactionId(txId);
      setShowRealWithdrawPopup(false);
      setShowRealWithdrawPasswordDialog(true);
    } catch (err) {
      const errorMessage = err.message || "Failed to create internal transfer.";
      if (showError) {
        showError("Error Occurred", errorMessage);
      } else {
        setRealWithdrawError(errorMessage);
      }
    } finally {
      setRealWithdrawProcessing(false);
    }
  };

  const handleRealWithdrawPasswordDialogClose = async (result, data = {}) => {
    setShowRealWithdrawPasswordDialog(false);
    setRealWithdrawTransactionId(null);

    if (result === "ok" || result === "need_admin") {
      if (showSuccess) {
        showSuccess(
          result === "ok" ? "Withdraw submitted" : "Withdraw pending",
          result === "ok"
            ? "Your account balance will be updated shortly."
            : "Withdraw is pending admin approval."
        );
      }
      try {
        await fetchAccounts();
      } catch (_) {
        // ignore refresh errors
      }
    } else if (result === "canceled") {
      if (showWarning) {
        showWarning("Action canceled", "Withdraw has been canceled.");
      }
    } else if (result === "failed") {
      if (showError) {
        const errorMessage =
          data.error?.response?.data?.MESSAGE ||
          data.error?.response?.data?.message ||
          data.responseData?.MESSAGE ||
          data.responseData?.message ||
          data.error?.message ||
          "Withdraw failed. Please try again.";
        showError("Error Occurred", errorMessage);
      }
    }

    // Always refresh accounts to keep wallet balances up to date
    if (result !== "ok" && result !== "need_admin") {
      try { await fetchAccounts(); } catch (_) {}
    }
  };

  const handleEditSaveClick = () => {
    if (!isEditMode) {
      setIsEditMode(true);
      return;
    }

    // API validates leverage with strict in_array([100, 200], true).
    const parseLeverageNumber = (value) => {
      if (!value) return 100;
      const raw = String(value).trim();
      let num = NaN;
      if (raw.includes(":") || raw.includes("/")) {
        const parts = raw.split(/[:/]/);
        if (parts.length === 2) num = parseInt(parts[1], 10);
      } else {
        num = parseInt(raw, 10);
      }
      if (num === 100 || num === 200) return num;
      return 200;
    };

    /** Normalize group id: object → GROUPE_ID, name → brokerGroups id, else number. */
    const resolveTradingGroupIdForApi = (raw) => {
      if (raw == null || raw === "") return null;
      if (typeof raw === "object" && !Array.isArray(raw)) {
        const fromObj =
          raw.GROUPE_ID ?? raw.groupe_id ?? raw.ID ?? raw.id ?? null;
        return resolveTradingGroupIdForApi(fromObj);
      }
      const asStr = String(raw).trim();
      if (!asStr || asStr === "null" || asStr === "undefined") return null;
      if (isNumericTradingGroupId(asStr)) return Number(asStr);
      const byName = (brokerGroups || []).find(
        (g) =>
          String(g?.name || "").trim().toLowerCase() === asStr.toLowerCase()
      );
      if (byName != null && isNumericTradingGroupId(byName.id)) {
        return Number(byName.id);
      }
      return null;
    };

    const isDemoEdit = accountType === "demo";

    if (isIbClientMode && loadingBrokerGroups) {
      if (showWarning) {
        showWarning(
          "Please wait",
          "Account details are still loading. Try again in a moment."
        );
      }
      return;
    }

    (async () => {
      try {
        // Resolve wallet id like Flutter: wallets.first.id from fresh infos.
        let walletId =
          editForm.walletId != null && String(editForm.walletId).trim() !== ""
            ? String(editForm.walletId).trim()
            : null;
        // Old working web always sent ACCOUNT_ENABLE: 1 for this endpoint.
        let accountEnableValue = 1;
        let tradingEnableValue =
          editForm.tradingEnabled === false || editForm.tradingEnabled === 0
            ? 0
            : 1;
        let referalBroker =
          editForm.referalBroker !== null &&
          editForm.referalBroker !== undefined &&
          String(editForm.referalBroker).trim() !== "" &&
          String(editForm.referalBroker).trim().toLowerCase() !== "null"
            ? String(editForm.referalBroker).trim()
            : null;
        let leverageForApi = parseLeverageNumber(editForm.leverage);
        let tradingGroupForApi = resolveTradingGroupIdForApi(
          editForm.tradingGroupId
        );

        if (editForm.internalId != null && String(editForm.internalId).trim() !== "") {
          try {
            const infosResp = await getTradingAccountInfos(
              editForm.internalId,
              isIbClientMode
            );
            const infosDecrypted =
              infosResp?.decryptedData ?? infosResp?.data ?? {};
            const infosCode = Number(
              infosDecrypted?.CODE ?? infosDecrypted?.code ?? 0
            );
            if (infosCode === AppConstants.responseOk || infosCode === 200) {
              const payload = infosDecrypted?.DATA || {};
              const accountPayload = payload.ACCOUNT || {};
              const accountInfo =
                accountPayload.account_information ||
                accountPayload.accountInformation ||
                {};
              const walletsRaw =
                accountPayload.wallet ?? accountPayload.wallets ?? null;
              const wallets = Array.isArray(walletsRaw)
                ? walletsRaw
                : walletsRaw && typeof walletsRaw === "object"
                  ? [walletsRaw]
                  : [];
              const firstWallet = wallets[0] || {};
              const walletIdFromApi =
                firstWallet.ID ??
                firstWallet.id ??
                firstWallet.WALLET_ID ??
                null;
              if (
                walletIdFromApi != null &&
                String(walletIdFromApi).trim() !== ""
              ) {
                walletId = String(walletIdFromApi).trim();
              }

              // Flutter: ACCOUNT_ENABLE / TRADING_ENABLE from loaded model.
              const accountEnabledRaw =
                accountPayload.IS_ACCOUNT_ENABLE ??
                accountPayload.isAccountEnable ??
                accountPayload.is_account_enable;
              if (accountEnabledRaw !== undefined && accountEnabledRaw !== null) {
                accountEnableValue =
                  accountEnabledRaw === 1 ||
                  accountEnabledRaw === true ||
                  accountEnabledRaw === "1"
                    ? 1
                    : 0;
              }

              const tradingEnabledRaw =
                accountInfo.IS_TRADING_ENABLE ??
                accountInfo.isTradingEnable ??
                accountInfo.is_trading_enable;
              if (typeof editForm.tradingEnabled === "boolean") {
                tradingEnableValue = editForm.tradingEnabled ? 1 : 0;
              } else if (
                tradingEnabledRaw !== undefined &&
                tradingEnabledRaw !== null
              ) {
                tradingEnableValue =
                  tradingEnabledRaw === 1 ||
                  tradingEnabledRaw === true ||
                  tradingEnabledRaw === "1"
                    ? 1
                    : 0;
              }

              const referalFromApi =
                accountInfo.REFERAL_BROKER ??
                accountInfo.referalBroker ??
                null;
              if (
                referalFromApi !== null &&
                referalFromApi !== undefined &&
                String(referalFromApi).trim() !== "" &&
                String(referalFromApi).trim().toLowerCase() !== "null"
              ) {
                referalBroker = String(referalFromApi).trim();
              }

              const leverageFromApi =
                accountInfo.LEVERAGE ?? accountInfo.leverage;
              if (editForm.leverage) {
                leverageForApi = parseLeverageNumber(editForm.leverage);
              } else if (leverageFromApi != null) {
                leverageForApi = parseLeverageNumber(leverageFromApi);
              }

              // If form group id was missing/invalid, fall back to current group.
              if (tradingGroupForApi == null) {
                tradingGroupForApi = resolveTradingGroupIdForApi(
                  accountInfo.TRADING_GROUP ?? accountInfo.tradingGroup
                );
              }
            }
          } catch (_) {
            // Keep values already resolved from the edit form.
          }
        }

        if (!walletId) {
          throw new Error(
            t("missing_wallet_id", {
              ns: "mobile",
              defaultValue:
                "Missing wallet ID for this account. Please close and reopen the edit dialog.",
            })
          );
        }

        if (isIbClientMode && tradingGroupForApi == null) {
          throw new Error(
            t("group_required", {
              ns: "mobile",
              defaultValue: "Please select a trading group.",
            })
          );
        }

        // Demo accounts: nickname-only update path (unchanged).
        if (isDemoEdit) {
          const nextName = String(editForm.name ?? "").trim();
          const initialName = String(editForm.initialName ?? "").trim();
          const shouldUpdateName = nextName !== "" && nextName !== initialName;

          if (!shouldUpdateName) {
            if (showWarning) {
              showWarning(
                "No changes applied",
                "Demo accounts currently support nickname update only. Leverage edit is not available."
              );
            }
            return;
          }

          const nameResp = await updateTradingAccountInfos({
            ID: walletId,
            NAME: nextName,
          });
          const nameDecrypted = nameResp?.decryptedData ?? nameResp?.data ?? {};
          const nameCode = Number(nameDecrypted?.CODE ?? nameDecrypted?.code ?? 0);
          if (nameCode !== 200 && nameCode !== AppConstants.responseOk) {
            const nameMessage =
              nameDecrypted?.MESSAGE ||
              nameDecrypted?.message ||
              "Failed to update trading name.";
            throw new Error(nameMessage);
          }

          invalidateTradingAccountsCache();
          await fetchAccounts(null, { forceRefresh: true });
          return;
        }

        // Flutter sends LEVERAGE as string, but production/local may validate with
        // strict in_array([100,200], true) — send integer. Other fields match Flutter.
        const requestBody = {
          ID: String(walletId),
          LEVERAGE: leverageForApi,
          REFERAL_BROKER: referalBroker,
          TRADING_GROUP:
            tradingGroupForApi != null ? String(tradingGroupForApi) : null,
          ACCOUNT_ENABLE: 1,
          TRADING_ENABLE: tradingEnableValue,
          ...(isIbClientMode ? { AGENT_VIEW: true } : {}),
        };

        // eslint-disable-next-line no-console
        console.log("[AccountsList] update-trading-infos request", requestBody);

        const response = await updateTradingAccountInfos(requestBody);
        const decrypted = response?.decryptedData ?? response?.data ?? {};
        const codeValue = Number(decrypted?.CODE ?? decrypted?.code ?? 0);
        const rawHasMessages = Boolean(
          response?.data?.MESSAGES && Array.isArray(response.data.MESSAGES)
        );

        // eslint-disable-next-line no-console
        console.log("[AccountsList] update-trading-infos response", {
          code: codeValue,
          encrypted: rawHasMessages,
          decrypted,
        });

        if (codeValue !== 200 && codeValue !== AppConstants.responseOk) {
          let message =
            decrypted?.MESSAGE ||
            decrypted?.message ||
            "";
          if (!message) {
            if (codeValue === AppConstants.invalidInputs || codeValue === 708) {
              message = t("invalid_inputs", {
                ns: "mobile",
                defaultValue: "Invalid inputs. Please check leverage and group.",
              });
            } else {
              message = t("try_again", {
                ns: "mobile",
                defaultValue: "Operation failed, please try again",
              });
            }
          }
          throw new Error(`${message} (code ${codeValue})`);
        }

        const tradingEnabledFromApi = parseTradingEnabledFromUpdateResponse(response);
        if (tradingEnabledFromApi !== null) {
          setEditForm((prev) => ({ ...prev, tradingEnabled: tradingEnabledFromApi }));
        }

        if (showSuccess) {
          showSuccess(
            t("successfully_operation", {
              ns: "mobile",
              defaultValue: "Operation Successfully Completed",
            }),
            ""
          );
        }

        invalidateTradingAccountsCache();
        await fetchAccounts(null, { forceRefresh: true });
      } catch (e) {
        if (showError) {
          showError("Error Occurred", e?.message || "Failed to update trading account.");
        }
      } finally {
        setShowEditPopup(false);
        setIsEditMode(false);
      }
    })();
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "0.00 $";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "0.00 $";
    return `${numValue.toFixed(2)} $`;
  };

  const formatLotsValue = (value) => {
    const numValue = Number(value);
    if (!Number.isFinite(numValue)) return "0.00";
    return numValue.toFixed(2);
  };

  const lookupAccountVolume = (keys) => {
    for (const key of keys) {
      if (key == null || key === "" || key === "-") continue;
      const str = String(key);
      if (Object.prototype.hasOwnProperty.call(volumeByAccount, str)) {
        return volumeByAccount[str];
      }
    }
    return 0;
  };

  const getAccountTypeLabel = (type) => {
    // Handle null/undefined
    if (type === null || type === undefined) {
      return "Unknown";
    }
    
    // Convert to number if it's a string
    const typeNum = typeof type === 'string' ? parseInt(type, 10) : type;
    
    // If conversion resulted in NaN, try string matching
    if (isNaN(typeNum)) {
      const typeStr = String(type).toUpperCase();
      if (typeStr === "REAL") return "Real";
      if (typeStr === "BROKER" || typeStr === "AGENT") return "Broker";
      if (typeStr === "DEMO") return "Demo";
      if (typeStr === "ADMIN") return "Admin";
      if (typeStr === "ADMIN_WALLET" || typeStr === "ADMINWALLET") return "Admin Wallet";
      if (typeStr === "WALLET") return "Wallet";
      return "Unknown";
    }
    
    // Use enum for numeric comparison
    switch (typeNum) {
      case AccountType.REAL:
        return "Real";
      case AccountType.BROKER:
        return "Broker";
      case AccountType.DEMO:
        return "Demo";
      case AccountType.ADMIN:
        return "Admin";
      case AccountType.ADMIN_WALLET:
        return "Admin Wallet";
      case AccountType.WALLET:
        return "Wallet";
      default:
        return "Unknown";
    }
  };

  const formatLeverage = (leverage) => {
    if (!leverage || leverage === "" || leverage === null || leverage === undefined) return "1/100";
    if (typeof leverage === "string" && leverage.includes("/")) return leverage;
    if (typeof leverage === "number") return `1/${leverage}`;
    return String(leverage);
  };

  const getAccountTypeFromResponse = (account) => {
    // Check TYPE field at root level or nested
    // Use !== undefined check to handle 0 (REAL) correctly
    const type = account.TYPE !== undefined ? account.TYPE : 
                 account.type !== undefined ? account.type :
                 account.ACCOUNT_TYPE !== undefined ? account.ACCOUNT_TYPE :
                 account.account_type !== undefined ? account.account_type : null;
    return getAccountTypeLabel(type);
  };

  const toNumberSafe = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const summaryTotals = useMemo(() => {
    const totals = accounts.reduce(
      (acc, account) => {
        const wallet =
          account.wallet && Array.isArray(account.wallet) && account.wallet.length > 0
            ? account.wallet[0]
            : {};

        acc.balance += toNumberSafe(
          wallet.CURRENT_BALENCE ?? wallet.CURRENT_BALANCE ?? wallet.BALANCE ?? account.BALANCE ?? account.balance
        );
        acc.credit += toNumberSafe(wallet.CREDIT ?? account.CREDIT ?? account.credit);
        acc.freeMargin += toNumberSafe(wallet.FREE_MARGIN ?? account.FREE_MARGIN ?? account.free_margin);
        acc.equity += toNumberSafe(wallet.EQUITY ?? account.EQUITY ?? account.equity);
        acc.lots += toNumberSafe(
          wallet.VOLUME ??
            wallet.LOTS ??
            wallet.VOLUME_LOTS ??
            account.VOLUME ??
            account.LOTS ??
            account.VOLUME_LOTS ??
            account.TOTAL_VOLUME
        );
        return acc;
      },
      { balance: 0, credit: 0, freeMargin: 0, equity: 0, lots: 0 }
    );

    return totals;
  }, [accounts]);

  const accountsPageSlice = useMemo(() => {
    if (!isIbClientMode) return accounts;
    const start = (currentPage - 1) * perPage;
    return accounts.slice(start, start + perPage);
  }, [isIbClientMode, accounts, currentPage, perPage]);

  // Size Trading Group to content on the current page, but never wider than
  // the original 190px column — long paths truncate with ellipsis + title tooltip.
  const tradingGroupColWidthPx = useMemo(() => {
    const cellFont = '400 15px "Public Sans", sans-serif';
    const headerFont = '500 12px Poppins, sans-serif';
    const headerLabel = t("trading_group", { ns: "mobile" });
    let maxPx = measureTextWidthPx(headerLabel, headerFont);

    for (const account of accountsPageSlice) {
      const accountInfo = account.account_information || account.accountInformation || {};
      const label = formatTradingGroupDisplay(
        resolveTradingGroupLabel(account, accountInfo),
        t
      );
      maxPx = Math.max(maxPx, measureTextWidthPx(label, cellFont));
    }

    // cell horizontal padding (20 + 20) + small buffer; hard cap at 190px
    return Math.max(120, Math.min(Math.ceil(maxPx) + 48, 190));
  }, [accountsPageSlice, t]);

  const accountsTableStyle = useMemo(
    () => ({ "--trading-group-col-width": `${tradingGroupColWidthPx}px` }),
    [tradingGroupColWidthPx]
  );

  const isLastTableRowBlack =
    accountsPageSlice.length > 0 ? accountsPageSlice.length % 2 === 0 : false;
  const paginationOppositeClass = isLastTableRowBlack
    ? "accounts-pagination--opposite-light"
    : "accounts-pagination--opposite-dark";

  // Skeleton loading dimensions (mirrors the real table layout).
  const skeletonColumnCount =
    6 + (hasAgent ? 1 : 0) + (showAccountVolumeColumn ? 1 : 0) + 1 + (!hideActionColumn ? 1 : 0);
  const skeletonRowCount = accountType === "demo" ? demoPerPage : 6;

  const selectedLeverageOption =
    LEVERAGE_DROPDOWN_OPTIONS.find((opt) => opt.value === editForm.leverage) || null;

  const groupDropdownOptions = loadingBrokerGroups
    ? []
    : brokerGroups.length === 0 && editForm.tradingGroupId != null && editForm.tradingGroupId !== ""
      ? [
          {
            value: String(editForm.tradingGroupId),
            label: editForm.group || String(editForm.tradingGroupId),
          },
        ]
      : brokerGroups.map((g) => ({
          value: String(g.id),
          label: g.name || String(g.id),
        }));

  const selectedGroupOption =
    groupDropdownOptions.find(
      (opt) =>
        String(opt.value) ===
        (editForm.tradingGroupId != null && editForm.tradingGroupId !== ""
          ? String(editForm.tradingGroupId)
          : "")
    ) || null;

  const formatVolumeRangeDate = (raw) => {
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(i18n.language || "en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const volumeRangeLabel =
    volumeRange.dateFrom && volumeRange.dateTo
      ? `${formatVolumeRangeDate(volumeRange.dateFrom)} – ${formatVolumeRangeDate(volumeRange.dateTo)}`
      : "";

  const openVolumeDateFilter = () => {
    setShowVolumeDateFilter(true);
  };

  const handleApplyVolumeRange = ({ dateFrom, dateTo, presetId }) => {
    const nextFrom = dateFrom || null;
    const nextTo = dateTo || null;
    setVolumeRange({
      dateFrom: nextFrom,
      dateTo: nextTo,
      presetId: presetId || "this_month",
    });
    setShowAccountVolumeColumn(Boolean(nextFrom && nextTo));
  };

  const volumeColumnHeader = (
    <div className="table-header-cell accounts-list-col-volume">
      <span>
        {t("ib_trading_history_total_volume", {
          ns: "mobile",
          defaultValue: "Total volume",
        })}
      </span>
      {volumeRangeLabel ? (
        <span className="accounts-list-volume-range">{volumeRangeLabel}</span>
      ) : null}
    </div>
  );

  const renderLotVolumeCard = ({ isSkeleton = false } = {}) => (
    <div
      className={`accounts-list-lot-card${isSkeleton ? " is-skeleton" : ""}${
        !isSkeleton && showAccountVolumeColumn ? " is-active" : ""
      }`}
      role={isSkeleton ? undefined : "button"}
      tabIndex={isSkeleton ? undefined : 0}
      aria-pressed={isSkeleton ? undefined : showAccountVolumeColumn}
      aria-label={
        volumeRangeLabel
          ? `${t("global_lot_volume", { ns: "mobile", defaultValue: "Global Lot Volume" })} ${volumeRangeLabel}`
          : `${t("global_lot_volume", { ns: "mobile", defaultValue: "Global Lot Volume" })}. ${t("ib_trading_history_select_dates", { ns: "mobile", defaultValue: "Select dates" })}`
      }
      onClick={isSkeleton ? undefined : openVolumeDateFilter}
      onKeyDown={
        isSkeleton
          ? undefined
          : (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openVolumeDateFilter();
              }
            }
      }
    >
      <div className="accounts-list-lot-card-inner">
        <div className="accounts-list-lot-card-copy">
          <div className="accounts-list-lot-card-title-row">
            <span className="accounts-list-lot-card-icon" aria-hidden>
              <i className="fa-solid fa-chart-pie" />
            </span>
            <div className="accounts-list-lot-card-heading">
              <strong>{t("global_lot_volume", { ns: "mobile", defaultValue: "Global Lot Volume" })}</strong>
              <span>
                {t("aggregate_across_all_accounts", {
                  ns: "mobile",
                  defaultValue: "Aggregate Across All Accounts",
                })}
              </span>
              {!isSkeleton && volumeRangeLabel ? (
                <span className="accounts-list-lot-card-range is-set">
                  <i className="fa-solid fa-calendar-days" aria-hidden />
                  {volumeRangeLabel}
                </span>
              ) : null}
            </div>
          </div>
          <div className="accounts-list-lot-card-metric">
            {isSkeleton ? (
              <span className="accounts-skeleton-bar accounts-skeleton-bar--lot-value" />
            ) : !volumeRangeLabel ? (
              <span className="accounts-list-lot-card-select">
                <i className="fa-solid fa-calendar-days" aria-hidden />
                {t("ib_trading_history_select_dates", {
                  ns: "mobile",
                  defaultValue: "Select dates",
                })}
              </span>
            ) : globalLotVolumeLoading ? (
              <span className="accounts-skeleton-bar accounts-skeleton-bar--lot-value" />
            ) : (
              <>
                <strong>{formatLotsValue(globalLotVolume)}</strong>
                <em>{t("lots_unit", { ns: "mobile", defaultValue: "LOTS" })}</em>
              </>
            )}
          </div>
        </div>
        <div className="accounts-list-lot-chart" aria-hidden>
          {LOT_VOLUME_CHART_BARS.map((bar, index) => (
            <span
              key={`lot-bar-${index}`}
              className={`accounts-list-lot-chart-bar${bar.accent ? " is-accent" : ""}${
                bar.glow ? " is-glow" : ""
              }`}
              style={{ height: `${bar.height}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`accounts-list-page accounts-list-standard-page ${
        isIbClientMode ? "ib-client-accounts-page" : ""
      }`}
    >
      <Sidebar user={user} />
      <div className="accounts-list-content">
        {loading ? (
          <div className="accounts-list-skeleton">
            {!hideAccountTypeSwitcher && (
              <div className="account-type-switcher-wrapper">
                <div className="account-type-switcher">
                  <span className="switcher-btn accounts-skeleton-pill" />
                  <span className="switcher-btn accounts-skeleton-pill" />
                </div>
              </div>
            )}

            <div className="accounts-list-summary-wrapper">
              <div className="accounts-list-summary-cards-row">
                <div className="accounts-list-summary-card">
                  <div className="accounts-list-summary-main">
                    <div className="accounts-list-summary-title-row">
                      <span>{t("total_balance", { ns: "mobile" })}</span>
                      <div className="accounts-list-summary-logo-badge">
                        <img
                          src={brandLogo}
                          alt="MFG Invest"
                          className="accounts-list-summary-logo"
                        />
                      </div>
                    </div>
                    <span className="accounts-skeleton-bar accounts-skeleton-bar--balance" />
                  </div>
                  <div className="accounts-list-summary-stats-row">
                    {[0, 1, 2].map((i) => (
                      <div className="accounts-list-summary-stat-box" key={`sk-stat-${i}`}>
                        <span className="accounts-skeleton-bar accounts-skeleton-bar--label" />
                        <span className="accounts-skeleton-bar accounts-skeleton-bar--value" />
                      </div>
                    ))}
                  </div>
                </div>
                {showGlobalLotVolume ? renderLotVolumeCard({ isSkeleton: true }) : null}
              </div>
            </div>

            <div className="accounts-table-container container-fluid px-0">
              <div className="table-top-header d-flex flex-wrap justify-content-between align-items-start gap-3">
                <div className="accounts-list-title-wrapper">
                  <h1 className="accounts-list-title">
                    {isIbClientMode
                      ? t("trading_accounts_title", { ns: "mobile" })
                      : resolvedCustomTitle || t("trading_accounts_title", { ns: "mobile" })}
                  </h1>
                </div>
              </div>
              <div className="table-responsive accounts-table-scroll">
                <div className="accounts-table" style={accountsTableStyle}>
                  <div className="table-header-row">
                    <div className="table-header-cell">{t("trading_id", { ns: "mobile" })}</div>
                    <div className="table-header-cell">{t("type", { ns: "mobile" })}</div>
                    <div className="table-header-cell">{t("balance", { ns: "mobile" })}</div>
                    <div className="table-header-cell">{t("equity", { ns: "mobile" })}</div>
                    <div className="table-header-cell">{t("free_margin", { ns: "mobile" })}</div>
                    <div className="table-header-cell">{t("leverage", { ns: "mobile" })}</div>
                    {hasAgent && (
                      <div className="table-header-cell agent-column">{t("agent", { ns: "mobile" })}</div>
                    )}
                    {showAccountVolumeColumn ? volumeColumnHeader : null}
                    <div className="table-header-cell trading-group-column">
                      {t("trading_group", { ns: "mobile" })}
                    </div>
                    {!hideActionColumn && (
                      <div className="table-header-cell action-column">{t("action", { ns: "mobile" })}</div>
                    )}
                  </div>
                  {Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                    <div className="table-row" key={`sk-row-${rowIndex}`}>
                      {Array.from({ length: skeletonColumnCount }).map((__, colIndex) => (
                        <div className="table-cell" key={`sk-cell-${rowIndex}-${colIndex}`}>
                          <span className="accounts-skeleton-bar accounts-skeleton-bar--cell" />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : (
          <>
            {isIbClientMode && (ibShowBackButton || customTitle) ? (
              <div className="ib-client-accounts-header-stack">
                {ibShowBackButton ? (
                  <div className="ib-client-accounts-back-row">
                    <button
                      type="button"
                      className="ib-back-btn ib-back-btn--in-flow"
                      onClick={() => navigate("/pages/ibDashboard/clients-list")}
                      aria-label="Back to clients"
                    >
                      <i className="fa-solid fa-arrow-left" aria-hidden />
                    </button>
                  </div>
                ) : null}
                {resolvedCustomTitle ? (
                    <div className="ib-client-accounts-banner-row">
                      <StatusBanner informational={{ title: resolvedCustomTitle }} />
                      <button
                        type="button"
                        className="ib-client-accounts-exit-btn"
                        onClick={() => navigate("/pages/ibDashboard/clients-list")}
                        aria-label="Exit client view"
                        title="Exit client view"
                      >
                        <span className="ib-client-accounts-exit-text">Exit</span>
                        <i className="fa-solid fa-xmark" aria-hidden />
                      </button>
                    </div>
                ) : null}
              </div>
            ) : null}
            {!hideAccountTypeSwitcher && (
              <div className="account-type-switcher-wrapper">
                <div className="account-type-switcher">
                  <button
                    className={`switcher-btn ${accountType === "real" ? "active" : ""}`}
                    onClick={() => {
                      setAccountType("real");
                      setCurrentPage(1);
                    }}
                  >
                    {t("real_accounts", { ns: "mobile" })}
                  </button>
                  <button
                    className={`switcher-btn ${accountType === "demo" ? "active" : ""}`}
                    onClick={() => {
                      setAccountType("demo");
                      setCurrentPage(1);
                    }}
                  >
                    {t("demo_accounts", { ns: "mobile" })}
                  </button>
                </div>
              </div>
            )}

            <div className="accounts-list-summary-wrapper">
              <div className="accounts-list-summary-cards-row">
                <div className="accounts-list-summary-card">
                  <div className="accounts-list-summary-main">
                    <div className="accounts-list-summary-title-row">
                      <span>{t("total_balance", { ns: "mobile" })}</span>
                      <div className="accounts-list-summary-logo-badge">
                        <img
                          src={brandLogo}
                          alt="MFG Invest"
                          className="accounts-list-summary-logo"
                        />
                      </div>
                    </div>
                    <strong>{formatCurrency(summaryTotals.balance)}</strong>
                  </div>
                  <div className="accounts-list-summary-stats-row">
                    <div className="accounts-list-summary-stat-box">
                      <span>{t("credit", { ns: "mobile" })}</span>
                      <strong>{formatCurrency(summaryTotals.credit)}</strong>
                    </div>
                    <div className="accounts-list-summary-stat-box">
                      <span>{t("free_margin", { ns: "mobile" })}</span>
                      <strong>{formatCurrency(summaryTotals.freeMargin)}</strong>
                    </div>
                    <div className="accounts-list-summary-stat-box">
                      <span>{t("equity", { ns: "mobile" })}</span>
                      <strong>{formatCurrency(summaryTotals.equity)}</strong>
                    </div>
                  </div>
                </div>
                {showGlobalLotVolume ? renderLotVolumeCard() : null}
              </div>

              {(!hideSummaryDeposit ||
                (accountType === "demo" && canDepositDemo) ||
                (accountType !== "demo" && !hideSummaryTransferWithdraw)) && (
                <div className="accounts-list-summary-actions">
                  {!hideSummaryDeposit && accountType !== "demo" && (
                    <button
                      type="button"
                      data-page-tour="account_deposit"
                      onClick={() => goToInternalTransfer("deposit")}
                    >
                      <i className="fa-solid fa-wallet" />
                      <span>{t("deposit", { ns: "mobile" })}</span>
                    </button>
                  )}
                  {accountType === "demo" && canDepositDemo && (
                    <button type="button" onClick={() => navigate("/pages/demo-deposit")}>
                      <i className="fa-solid fa-wallet" />
                      <span>{t("deposit", { ns: "mobile" })}</span>
                    </button>
                  )}
                  {accountType !== "demo" && !hideSummaryTransferWithdraw && (
                    <button
                      type="button"
                      data-page-tour="account_transfer"
                      onClick={() => navigate("/pages/transfer")}
                    >
                      <i className="fa-solid fa-right-left" />
                      <span>{t("transfer", { ns: "mobile" })}</span>
                    </button>
                  )}
                  {accountType !== "demo" && !hideSummaryTransferWithdraw && (
                    <button
                      type="button"
                      data-page-tour="account_withdraw"
                      onClick={() => goToInternalTransfer("withdraw")}
                    >
                      <i className="fa-solid fa-arrow-down" />
                      <span>{t("withdraw", { ns: "mobile" })}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
            
            <div className="accounts-table-container container-fluid px-0">
              <div className="table-top-header d-flex flex-wrap justify-content-between align-items-start gap-3">
                <div className="accounts-list-title-wrapper">
                  <h1 className="accounts-list-title">
                    {isIbClientMode
                      ? t("trading_accounts_title", { ns: "mobile" })
                      : resolvedCustomTitle || t("trading_accounts_title", { ns: "mobile" })}
                  </h1>
                </div>
                <div className="accounts-list-actions d-flex flex-wrap align-items-center">
                  {isSearchActive ? (
                    <div className="search-container" ref={searchContainerRef}>
                      <input
                        type="text"
                        className="search-input"
                        placeholder={t("search_by_account_id_or_name", { ns: "mobile" })}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleSearchKeyPress}
                        autoFocus
                      />
                      {searchTerm && (
                        <button 
                          className="search-clear-btn" 
                          onClick={handleClearSearch}
                          aria-label="Clear search"
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      )}
                      <button 
                        className="search-btn" 
                        onClick={handleSearch}
                        aria-label="Search"
                      >
                        <i className="fa-solid fa-search"></i>
                      </button>
                    </div>
                  ) : (
                    <>
                      <button 
                        className="action-icon-btn" 
                        onClick={handleToggleSearch}
                        aria-label="Search"
                      >
                        <i className="fa-solid fa-search"></i>
                      </button>
                      {!hideNewAccountButton && (
                        <div className="new-account-btn-wrapper" data-page-tour="add_account">
                          <button 
                            className="new-account-btn" 
                            onClick={handleGoToAddPage}
                            disabled={
                              approvalLocked ||
                              (accountType !== "demo" &&
                                accountLimit !== null &&
                                accountLimit !== undefined &&
                                (totalAccounts || accounts.length) >= accountLimit)
                            }
                          >
                            <i className="fa-solid fa-plus"></i> {t("new_account", { ns: "mobile" })}
                          </button>
                          {accountType !== "demo" &&
                            accountLimit !== null &&
                            accountLimit !== undefined &&
                            (totalAccounts || accounts.length) >= accountLimit && (
                            <div
                              className="new-account-tooltip"
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                openSupportChatOnCurrentPage(
                                  navigate,
                                  location.pathname,
                                  location.search
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openSupportChatOnCurrentPage(
                                    navigate,
                                    location.pathname,
                                    location.search
                                  );
                                }
                              }}
                              title="Open support chat"
                            >
                              You have exceeded the account limit. Please contact the admin.
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="table-responsive accounts-table-scroll">
              <div className="accounts-table" style={accountsTableStyle}>
                <div className="table-header-row">
                  <div className="table-header-cell">{t("trading_id", { ns: "mobile" })}</div>
                  <div className="table-header-cell">{t("type", { ns: "mobile" })}</div>
                  <div className="table-header-cell">{t("balance", { ns: "mobile" })}</div>
                  <div className="table-header-cell">{t("equity", { ns: "mobile" })}</div>
                  <div className="table-header-cell">{t("free_margin", { ns: "mobile" })}</div>
                  <div className="table-header-cell">{t("leverage", { ns: "mobile" })}</div>
                  {hasAgent && <div className="table-header-cell agent-column">{t("agent", { ns: "mobile" })}</div>}
                  {showAccountVolumeColumn ? volumeColumnHeader : null}
                  <div className="table-header-cell trading-group-column">{t("trading_group", { ns: "mobile" })}</div>
                  {!hideActionColumn && (
                    <div className="table-header-cell action-column">{t("action", { ns: "mobile" })}</div>
                  )}
                </div>
                {accounts && accounts.length > 0 ? (
                  accountsPageSlice.map((account, index) => {
                    const rowAccountType = getAccountTypeFromResponse(account);
                    // Check if account type is BROKER using enum
                    const typeValue = account.TYPE || account.type || account.ACCOUNT_TYPE || account.account_type;
                    const typeNum = typeof typeValue === 'string' ? parseInt(typeValue, 10) : typeValue;
                    const isBroker = !isNaN(typeNum) && typeNum === AccountType.BROKER;
                    
                    // Extract all possible field name variations from the API response
                    // Handle nested structure: account_information, wallet array, etc.
                    const accountInfo = account.account_information || account.accountInformation || {};
                    const wallet = account.wallet && Array.isArray(account.wallet) && account.wallet.length > 0 ? account.wallet[0] : {};
                    const currencyInfo = wallet.currencies || {};
                    const groupInfo =
                      (accountInfo.group && typeof accountInfo.group === "object" && !Array.isArray(accountInfo.group)
                        ? accountInfo.group
                        : null) ||
                      (accountInfo.Group && typeof accountInfo.Group === "object" && !Array.isArray(accountInfo.Group)
                        ? accountInfo.Group
                        : null) ||
                      (accountInfo.GROUP && typeof accountInfo.GROUP === "object" && !Array.isArray(accountInfo.GROUP)
                        ? accountInfo.GROUP
                        : null) ||
                      {};

                    const tradingId = accountInfo.TRADING_ID || account.TRADING_ID || account.trading_id || account.ID || account.id || account.ACCOUNT_ID || "-";
                    const nickRaw =
                      account.NICK ||
                      account.NICK_NAME ||
                      account.nick ||
                      account.nick_name ||
                      account.NICKNAME ||
                      accountInfo.NAME ||
                      accountInfo.name ||
                      "";
                    const nick = nickRaw === "-" ? "" : String(nickRaw).trim();
                    const balance = wallet.CURRENT_BALENCE || wallet.CURRENT_BALANCE || wallet.BALANCE || account.BALANCE || account.balance || 0;
                    const equity = wallet.EQUITY || account.EQUITY || account.equity || 0;
                    const freeMargin = wallet.FREE_MARGIN || account.FREE_MARGIN || account.free_margin || 0;
                    const leverage = accountInfo.LEVERAGE || account.LEVERAGE || account.leverage || 200;
                    const agent = accountInfo.REFERAL_BROKER || account.REFERAL_BROKER || account.AGENT || account.agent || account.AGENT_ID || account.agent_id || "-";
                    const tradingGroup = resolveTradingGroupLabel(account, accountInfo);
                    const tradingGroupDisplay = formatTradingGroupDisplay(tradingGroup, t);
                    const accountVolume = lookupAccountVolume([
                      accountInfo.TRADING_ID,
                      tradingId,
                      accountInfo.ACCOUNT_ID,
                      accountInfo.account_id,
                      account.ACCOUNT_ID,
                      account.account_id,
                      account.ID,
                      account.id,
                    ]);
                    const tradingEnabled = parseTradingEnabledFromAccount(account, accountInfo);
                    const accountEnabledRaw =
                      account.IS_ACCOUNT_ENABLE ??
                      account.isAccountEnable ??
                      account.is_account_enable;
                    const accountEnabled =
                      accountEnabledRaw === undefined || accountEnabledRaw === null
                        ? true
                        : accountEnabledRaw === 1 ||
                          accountEnabledRaw === true ||
                          accountEnabledRaw === "1";
                    const editPopupData = {
                      tradingId,
                      internalId:
                        accountInfo.ACCOUNT_ID ||
                        accountInfo.account_id ||
                        account.ACCOUNT_ID ||
                        account.account_id ||
                        account.ID ||
                        account.id ||
                        null,
                      walletId: wallet.ID || wallet.id || wallet.WALLET_ID || null,
                      // Flutter uses account_information.REFERAL_BROKER (MT5 trading id string), not DB id.
                      referalBroker:
                        accountInfo.REFERAL_BROKER ||
                        account.REFERAL_BROKER ||
                        accountInfo.REFERAL_BROKER_ID ||
                        account.REFERAL_BROKER_ID ||
                        null,
                      tradingGroupId:
                        // Flutter uses account_information.TRADING_GROUP (GROUPE_ID FK).
                        accountInfo.TRADING_GROUP ??
                        accountInfo.tradingGroup ??
                        groupInfo.GROUPE_ID ??
                        groupInfo.groupe_id ??
                        account.TRADING_GROUP ??
                        null,
                      group: tradingGroup,
                      leverage,
                      name: nick,
                      tradingEnabled: tradingEnabled,
                      accountEnabled,
                      isBroker,
                      updateIds: [
                        wallet.ID,
                        wallet.id,
                        wallet.WALLET_ID,
                      ].filter((id) => id != null && id !== ""),
                    };
                    
                    return (
                      <div key={account.ID || account.id || account.TRADING_ID || index} className="table-row">
                        <div className="table-cell table-cell-trading-id">
                          <button
                            type="button"
                            className="trading-id-main trading-id-link"
                            onClick={() => canEditAccountRow && handleOpenEditPopup(editPopupData)}
                            disabled={!canEditAccountRow}
                            aria-label={`${t("edit", { ns: "mobile", defaultValue: "Edit" })} ${tradingId}`}
                          >
                            {tradingId}
                          </button>
                          {nick ? <span className="trading-id-nickname">{nick}</span> : null}
                        </div>
                        <div className="table-cell">
                          <span className={`type-badge ${
                            rowAccountType === "Broker" ? "type-agent" : 
                            rowAccountType === "Unknown" ? "type-unknown" : 
                            "type-real"
                          }`}>
                            {rowAccountType}
                          </span>
                        </div>
                        <div className="table-cell">{formatCurrency(balance)}</div>
                        <div className="table-cell">{formatCurrency(equity)}</div>
                        <div className="table-cell">{formatCurrency(freeMargin)}</div>
                        <div className="table-cell">{formatLeverage(leverage)}</div>
                        {hasAgent && <div className="table-cell agent-column">{agent}</div>}
                        {showAccountVolumeColumn && (
                          <div
                            className="table-cell accounts-list-col-volume"
                            title={volumeRangeLabel || undefined}
                          >
                            {globalLotVolumeLoading ? (
                              <span className="accounts-skeleton-bar accounts-skeleton-bar--cell" />
                            ) : (
                              <span>
                                {formatLotsValue(accountVolume)}{" "}
                                {t("ib_trading_history_lots", { ns: "mobile", defaultValue: "lots" })}
                              </span>
                            )}
                          </div>
                        )}
                        <div
                          className="table-cell trading-group-column"
                          title={tradingGroupDisplay}
                        >
                          {tradingGroupDisplay}
                        </div>
                        {!hideActionColumn && (
                          <div className="table-cell action-column">
                            <div className="action-buttons">
                            {isBroker && (
                              <button
                                className="action-btn"
                                aria-label="Invite"
                                title="Invite"
                                onClick={() => handleOpenSharePopup(tradingId)}
                              >
                                <i className="fa-solid fa-share-nodes"></i>
                              </button>
                            )}
                            {canDeposit && accountType !== "demo" && (
                              <button
                                className="action-btn"
                                aria-label={t("deposit", { ns: "mobile", defaultValue: "Deposit" })}
                                title={t("deposit", { ns: "mobile", defaultValue: "Deposit" })}
                                onClick={() => goToInternalTransfer("deposit", editPopupData.walletId)}
                              >
                                <i className="fa-solid fa-arrow-up"></i>
                                <span className="action-btn-label">{t("deposit", { ns: "mobile", defaultValue: "Deposit" })}</span>
                              </button>
                            )}
                            {canDepositDemo && accountType === "demo" && (
                              <button
                                className="action-btn"
                                aria-label={t("deposit", { ns: "mobile", defaultValue: "Deposit" })}
                                title={t("deposit", { ns: "mobile", defaultValue: "Deposit" })}
                                onClick={() =>
                                  navigate("/pages/demo-deposit", {
                                    state: {
                                      walletId: editPopupData.walletId,
                                      tradingId,
                                    },
                                  })
                                }
                              >
                                <i className="fa-solid fa-arrow-up"></i>
                                <span className="action-btn-label">{t("deposit", { ns: "mobile", defaultValue: "Deposit" })}</span>
                              </button>
                            )}
                            {canWithdraw && accountType !== "demo" && !hideRowWithdraw && !(isBroker && agentWithdrawalDisabled) && (
                              <button
                                className="action-btn action-btn--withdraw"
                                aria-label={t("withdraw", { ns: "mobile", defaultValue: "Withdraw" })}
                                title={t("withdraw", { ns: "mobile", defaultValue: "Withdraw" })}
                                onClick={() => goToInternalTransfer("withdraw", editPopupData.walletId)}
                              >
                                <i className="fa-solid fa-arrow-down"></i>
                                <span className="action-btn-label">{t("withdraw", { ns: "mobile", defaultValue: "Withdraw" })}</span>
                              </button>
                            )}
                            {canEditAccountRow && (
                              <button
                                className="action-btn"
                                aria-label={t("edit", { ns: "mobile", defaultValue: "Edit" })}
                                title={t("edit", { ns: "mobile", defaultValue: "Edit" })}
                                onClick={() => handleOpenEditPopup(editPopupData)}
                              >
                                <i className="fa-solid fa-pencil"></i>
                                <span className="action-btn-label">Edit</span>
                              </button>
                            )}
                            {accountType !== "demo" && !isIbClientMode && !isBroker && canViewAccountHistory && (
                              <button
                                className="action-btn"
                                aria-label={t("history", { ns: "mobile", defaultValue: "History" })}
                                title={t("history", { ns: "mobile", defaultValue: "History" })}
                                onClick={() =>
                                  navigate(`/pages/accounts/history/${encodeURIComponent(tradingId)}`, {
                                    state: { tradingId, nick },
                                  })
                                }
                              >
                                <i className="fa-solid fa-clock-rotate-left"></i>
                                <span className="action-btn-label">
                                  {t("history", { ns: "mobile", defaultValue: "History" })}
                                </span>
                              </button>
                            )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="table-row no-data-row">
                    <div className="table-cell no-data" style={{ width: "100%" }}>{t("no_accounts_found", { ns: "mobile" })}</div>
                  </div>
                )}
              </div>
              </div>
              <div className={`accounts-pagination ${paginationOppositeClass}`}>
              <div className="pagination-controls accounts-pagination-controls">
                <button 
                  className="pagination-btn" 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <i className={`fa-solid ${isRtl ? "fa-chevron-right" : "fa-chevron-left"}`}></i>
                </button>
                <button className="pagination-btn active">
                  <span>{currentPage}</span>
                </button>
                <button 
                  className="pagination-btn" 
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage >= Math.ceil((totalAccounts || accounts.length) / (accountType === "demo" ? demoPerPage : perPage))}
                >
                  <i className={`fa-solid ${isRtl ? "fa-chevron-left" : "fa-chevron-right"}`}></i>
                </button>
              </div>
            </div>
            </div>
          </>
        )}
      </div>
      {showEditPopup && (
        <div className="account-edit-overlay" onClick={handleCloseEditPopup}>
          <div
            className="account-edit-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="account-edit-header-row">
              <div className="account-edit-account-info">
                <span className="account-edit-account-label">Account:</span>
                <span className="account-edit-account-value">
                  #{editForm.accountId || "-"}
                </span>
              </div>
              <div className="account-edit-links">
                {!editForm.isBroker && (
                  <button
                    type="button"
                    className="account-edit-link-btn"
                    onClick={() => handleOpenResetPasswordPopup(0)}
                  >
                    <img
                      src={keyIcon}
                      alt="Reset main password"
                      className="account-edit-key-icon"
                    />
                    <span>Reset Main Password</span>
                  </button>
                )}
                <button
                  type="button"
                  className="account-edit-link-btn"
                  onClick={() => handleOpenResetPasswordPopup(1)}
                >
                  <img
                    src={keyIcon}
                    alt="Reset investor password"
                    className="account-edit-key-icon"
                  />
                  <span>Reset Investor Password</span>
                </button>
              </div>
            </div>

            <div className="account-edit-body">
              <div className="account-edit-field-row">
                <div className="account-edit-field">
                  <label>{t("group", { ns: "mobile", defaultValue: "Group" })}</label>
                  {isIbClientMode ? (
                    <>
                      <CustomDropdown
                        options={groupDropdownOptions}
                        value={selectedGroupOption}
                        onChange={(option) => {
                          const nextId = option?.value ?? "";
                          const selected = brokerGroups.find(
                            (g) => String(g.id) === String(nextId)
                          );
                          const normalizedId = isNumericTradingGroupId(nextId)
                            ? Number(nextId)
                            : nextId || null;
                          setEditForm((prev) => ({
                            ...prev,
                            tradingGroupId: normalizedId,
                            group: selected?.name || option?.label || prev.group,
                          }));
                        }}
                        placeholder={
                          loadingBrokerGroups
                            ? `${t("loading", { ns: "mobile", defaultValue: "Loading" })}...`
                            : t("group", { ns: "mobile", defaultValue: "Group" })
                        }
                        disabled={
                          !isEditMode ||
                          !canEditTradingGroup ||
                          loadingBrokerGroups ||
                          accountType === "demo"
                        }
                        className="account-edit-dropdown"
                      />
                      {(!groupMode || editForm.ableToEdit === false) && (
                        <div className="account-edit-field-hint" style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
                          {t("group_modification_info", {
                            ns: "mobile",
                            defaultValue: "The group can be modified by the admin only.",
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <input
                      type="text"
                      value={editForm.group}
                      readOnly
                      disabled
                    />
                  )}
                </div>
                <div className="account-edit-field">
                  <label>Leverage</label>
                  <CustomDropdown
                    options={LEVERAGE_DROPDOWN_OPTIONS}
                    value={selectedLeverageOption}
                    onChange={(option) =>
                      handleEditFormChange("leverage", option?.value || "")
                    }
                    placeholder="Select leverage"
                    disabled={!isEditMode || !canUpdateLeverageEffective || accountType === "demo"}
                    className="account-edit-dropdown"
                  />
                </div>
              </div>

              <div className="account-edit-field-row">
                <div className="account-edit-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) =>
                      handleEditFormChange("name", e.target.value)
                    }
                    disabled={!isEditMode}
                  />
                </div>
                <div className="account-edit-field account-edit-toggle-field">
                  <label>Trading Enabled</label>
                  <button
                    type="button"
                    className={`account-edit-toggle ${
                      editForm.tradingEnabled ? "on" : "off"
                    }`}
                    onClick={handleToggleTradingEnabled}
                    disabled={!isEditMode || !canEnableTradingEffective || accountType === "demo"}
                  >
                    <span className="account-edit-toggle-knob" />
                  </button>
                </div>
              </div>
            </div>

            <button
              type="button"
              className={`account-edit-main-btn ${isEditMode ? "save" : ""}`}
              onClick={handleEditSaveClick}
              disabled={loadingBrokerGroups}
            >
              <i className="fa-solid fa-pen" />
              <span>
                {loadingBrokerGroups
                  ? t("loading", { ns: "mobile", defaultValue: "Loading" })
                  : isEditMode
                    ? "Save"
                    : "Edit"}
              </span>
            </button>
          </div>
        </div>
      )}
      <Popup
        isOpen={showSharePopup}
        onClose={handleConfirmSharePopup}
        onCancel={handleCloseSharePopup}
        showCancel={true}
        title="Share Broker Information Link"
        message={
          shareBrokerId
            ? `Copy and share the following link for trading ID ${shareBrokerId}:`
            : "Copy and share the following account information link:"
        }
        instruction=""
        buttonText=""
        cancelButtonText="Cancel"
        icon="fa-share-nodes"
      >
        <div className="cash-in-clipboard-container">
          <label className="cash-in-clipboard-label">Clipboard</label>
          <div className="cash-in-clipboard-field">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="cash-in-clipboard-input"
            />
            <button
              type="button"
              onClick={handleCopyShareUrl}
              className="cash-in-clipboard-copy-button"
              title="Copy to clipboard"
            >
              <i className="fa-solid fa-copy" />
            </button>
            <button
              type="button"
              onClick={handleShareWhatsApp}
              className="cash-in-clipboard-copy-button cash-in-clipboard-whatsapp-button"
              title="Share on WhatsApp"
            >
              <i className="fa-brands fa-whatsapp" />
            </button>
          </div>
        </div>
      </Popup>
      <Popup
        isOpen={showResetPasswordPopup}
        onClose={handleConfirmResetPassword}
        onCancel={handleCloseResetPasswordPopup}
        showCancel={true}
        title={
          resetPasswordType === 0
            ? "Reset Main Password"
            : resetPasswordType === 1
            ? "Reset Investor Password"
            : ""
        }
        message={
          resetPasswordType === 0
            ? `Are you sure you want to reset the main MT5 password for account #${editForm.accountId}?`
            : resetPasswordType === 1
            ? `Are you sure you want to reset the investor MT5 password for account #${editForm.accountId}?`
            : ""
        }
        instruction="You can cancel if you clicked this by mistake."
        buttonText="Confirm"
        cancelButtonText="Cancel"
        icon="fa-key"
      />
      <Popup
        isOpen={showAddAccountPopup}
        onClose={handleConfirmAddAccount}
        onCancel={handleCloseAddAccountPopup}
        showCancel={true}
        title={accountType === "demo" ? "Add Demo Account" : "Add Real Account"}
        message={
          accountType === "demo"
            ? "Are you sure you need to add a new Demo Account?"
            : "Are you sure you need to add a new Real Account?"
        }
        instruction="You can cancel if you clicked this by mistake."
        buttonText={creatingAccount ? "Creating..." : "Yes"}
        cancelButtonText="Cancel"
        icon="fa-plus"
      />

      {showRealDepositPopup && (
        <div className="popup-overlay" onClick={handleCloseRealDepositPopup}>
          <div
            className="popup-content demo-deposit-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-body-content">
              <div className="popup-icon-wrapper">
                <i className="fa-solid fa-arrow-up"></i>
              </div>
              <h2 className="popup-title">{t("deposit_from_usd", { ns: "mobile", defaultValue: "Deposit from USD" })}</h2>
              {realDepositAccountId && (
                <p className="popup-message">
                  Add amount to account #{realDepositAccountId}
                </p>
              )}
              <div className="cash-in-form-group">
                <label className="cash-in-form-label">{t("amount", { ns: "mobile", defaultValue: "Amount" })}</label>
                <div className="cash-in-input-wrapper">
                  <span className="cash-in-input-icon">
                    <i className="fa-solid fa-dollar-sign"></i>
                  </span>
                  <CustomInput
                    type="text"
                    placeholder={t("enter_amount", { ns: "mobile", defaultValue: "Enter Amount" })}
                    value={realDepositAmount}
                    onChange={(e) => {
                      setRealDepositAmount(e.target.value);
                      if (realDepositError) setRealDepositError(null);
                    }}
                    className={`cash-in-input ${
                      realDepositError ? "cash-in-input-error" : ""
                    }`}
                    disabled={realDepositProcessing}
                  />
                </div>
                {realDepositError && (
                  <div className="demo-deposit-error">{realDepositError}</div>
                )}
              </div>
            </div>
            <div className="popup-footer">
              <button
                className="popup-btn-cancel"
                onClick={handleCloseRealDepositPopup}
                disabled={realDepositProcessing}
              >
                Cancel
              </button>
              <button
                className="popup-btn-primary"
                onClick={handleConfirmRealDeposit}
                disabled={realDepositProcessing}
              >
                {realDepositProcessing ? "Processing..." : "Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRealWithdrawPopup && (
        <div className="popup-overlay" onClick={handleCloseRealWithdrawPopup}>
          <div
            className="popup-content demo-deposit-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-body-content">
              <div className="popup-icon-wrapper">
                <i className="fa-solid fa-arrow-down"></i>
              </div>
              <h2 className="popup-title">{t("withdraw_to_usd", { ns: "mobile", defaultValue: "Withdraw to USD" })}</h2>
              {realWithdrawAccountId && (
                <p className="popup-message">
                  Withdraw from account #{realWithdrawAccountId} to your USD wallet
                </p>
              )}
              <div className="cash-in-form-group">
                <label className="cash-in-form-label">{t("amount", { ns: "mobile", defaultValue: "Amount" })}</label>
                <div className="cash-in-input-wrapper">
                  <span className="cash-in-input-icon">
                    <i className="fa-solid fa-dollar-sign"></i>
                  </span>
                  <CustomInput
                    type="text"
                    placeholder={t("enter_amount", { ns: "mobile", defaultValue: "Enter Amount" })}
                    value={realWithdrawAmount}
                    onChange={(e) => {
                      setRealWithdrawAmount(e.target.value);
                      if (realWithdrawError) setRealWithdrawError(null);
                    }}
                    className={`cash-in-input ${
                      realWithdrawError ? "cash-in-input-error" : ""
                    }`}
                    disabled={realWithdrawProcessing}
                  />
                </div>
                {realWithdrawError && (
                  <div className="demo-deposit-error">{realWithdrawError}</div>
                )}
              </div>
            </div>
            <div className="popup-footer">
              <button
                className="popup-btn-cancel"
                onClick={handleCloseRealWithdrawPopup}
                disabled={realWithdrawProcessing}
              >
                Cancel
              </button>
              <button
                className="popup-btn-primary"
                onClick={handleRealWithdrawConfirm}
                disabled={realWithdrawProcessing}
              >
                {realWithdrawProcessing ? "Processing..." : "Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDemoDepositPopup && (
        <div className="popup-overlay" onClick={handleCloseDemoDepositPopup}>
          <div
            className="popup-content demo-deposit-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-body-content">
              <div className="popup-icon-wrapper">
                <i className="fa-solid fa-arrow-up"></i>
              </div>
              <h2 className="popup-title">{t("demo_deposit", { ns: "mobile", defaultValue: "Demo Deposit" })}</h2>
              {demoDepositAccountId && (
                <p className="popup-message">
                  Add amount to demo account #{demoDepositAccountId}
                </p>
              )}
              <div className="cash-in-form-group">
                <label className="cash-in-form-label">{t("amount", { ns: "mobile", defaultValue: "Amount" })}</label>
                <div className="cash-in-input-wrapper">
                  <span className="cash-in-input-icon">
                    <i className="fa-solid fa-dollar-sign"></i>
                  </span>
                  <CustomInput
                    type="text"
                    placeholder={t("enter_amount", { ns: "mobile", defaultValue: "Enter Amount" })}
                    value={demoDepositAmount}
                    onChange={(e) => {
                      setDemoDepositAmount(e.target.value);
                      if (demoDepositError) setDemoDepositError(null);
                    }}
                    className={`cash-in-input ${
                      demoDepositError ? "cash-in-input-error" : ""
                    }`}
                    disabled={demoDepositProcessing}
                  />
                </div>
                {demoDepositError && (
                  <div className="demo-deposit-error">{demoDepositError}</div>
                )}
              </div>
            </div>
            <div className="popup-footer">
              <button
                className="popup-btn-cancel"
                onClick={handleCloseDemoDepositPopup}
                disabled={demoDepositProcessing}
              >
                Cancel
              </button>
              <button
                className="popup-btn-primary"
                onClick={handleConfirmDemoDeposit}
                disabled={demoDepositProcessing}
              >
                {demoDepositProcessing ? "Processing..." : "Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}

      <TransactionPasswordDialog
        open={showDemoPasswordDialog && !!demoDepositTransactionId}
        onClose={handleDemoPasswordDialogClose}
        mode="demo_deposit"
        requestToken={demoDepositTransactionId || ""}
        firstMessage={
          demoDepositAmount
            ? `You will add ${demoDepositAmount} ${demoDepositCurrencySymbol || ""} to your demo account.`
            : "Confirm demo deposit."
        }
        secondMessage=""
      />
      <TransactionPasswordDialog
        open={showRealPasswordDialog && !!realDepositTransactionId}
        onClose={handleRealPasswordDialogClose}
        mode="internal_transfer"
        requestToken={realDepositTransactionId || ""}
        firstMessage={
          realDepositAmount
            ? `You will transfer ${realDepositAmount} ${
                realDepositCurrencySymbol || ""
              } from your USD wallet to account #${realDepositAccountId || ""}.`
            : "Confirm internal transfer from USD wallet."
        }
        secondMessage=""
      />
      <TransactionPasswordDialog
        open={showRealWithdrawPasswordDialog && !!realWithdrawTransactionId}
        onClose={handleRealWithdrawPasswordDialogClose}
        mode="internal_transfer"
        requestToken={realWithdrawTransactionId || ""}
        firstMessage={
          realWithdrawAmount
            ? `You will transfer ${realWithdrawAmount} ${
                realWithdrawCurrencySymbol || ""
              } from account #${realWithdrawAccountId || ""} to your USD wallet.`
            : "Confirm internal transfer to USD wallet."
        }
        secondMessage=""
      />
      {showGlobalLotVolume ? (
        <DateRangeFilter
          key={`${accountType}-${volumeRange.dateFrom || ""}-${volumeRange.dateTo || ""}`}
          isOpen={showVolumeDateFilter}
          onClose={() => setShowVolumeDateFilter(false)}
          onApply={handleApplyVolumeRange}
          initialRange={volumeRange}
        />
      ) : null}
    </div>
  );
};

export default AccountsList;
